import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageItem } from '../types';
import { searchNetSuiteItems, getThreadOrderItems, getThreadRmaItems, submitItemCorrection, updateOrderLine, updateRmaLine, getReturnReasons, getDiscountItems, getMarketingItems, addPreviewLine, updatePreviewCredit, deletePreviewLine, setPreviewLineItem, perUnitLbFromName, getSoPreviewBundle, getItemWeightsAndPrices, getLeveledPrices, getPreviewPromoLines, getDropshipVariant, fillInvoicePrices, getSoPreviewPrefs, saveSoPreviewPrefs, getCustomerSources, getDeliveredPrices } from '../services/api';
import type { SoPreviewBundle, SoHeaderFields, LeveledPriceSchedule, PromoLine, RmaSource } from '../services/api';

interface EditableItem {
  id: number;
  shorthand: string;
  resolved_item: string | null;
  item_id: number | null;
  quantity: number;
  unit: string | null;
  // SO-preview v2: the WANTED pair the customer asked for (e.g. 2 / "pallets").
  // Editable in the row; NULL on old rows -> falls back to quantity/unit.
  entered_qty: number | null;
  entered_unit: string | null;
  // Pre-conversion pallet count preserved at intake when a pallet line was
  // converted to bags (quantity/unit hold the base bags). Non-null => this row
  // did a pallet->bags conversion even when entered_* are NULL (old rows).
  original_pallet_qty?: number | null;
  // Inferred pallet count (quantity / authoritative bags-per-pallet) from the
  // thread-items endpoint, for converted bag lines with no stored count.
  inferred_pallet_qty?: number | null;
  // The big-unit NOUN for the count label ('case' | 'pallet'), from the thread-
  // items endpoint. When absent, the label falls back to 'pallet'.
  orig_unit?: string | null;
  // PACK DATA from the thread-items endpoint enrichment (message_items_api
  // _enrich_inferred_pallets), used to convert a MANUAL pallets/cases entry to
  // base units INSTANTLY (optimistic, client-side) instead of waiting on the
  // PATCH round-trip. Mirrors the server rules: bags_per_pallet is used only when
  // NOT the default guess (bags_per_pallet_is_default=false); case_qty is null
  // when there's no case pack (then we never guess — server keeps 'case' + flags).
  bags_per_pallet?: number | null;
  bags_per_pallet_is_default?: boolean;
  case_qty?: number | null;
  base_unit?: string | null;
  // The data-driven conversion the last PATCH returned (e.g. "2 pallets -> 100
  // bags"). Empty when there is no conversion; shown inline under the row.
  conversion_display?: string;
  // True when the server flagged the line for human review (e.g. unknown case
  // pack with no default) -> small warning affordance, mirroring low-confidence.
  needs_review?: boolean;
  resolution_confidence: number | null;
  removed: boolean;
  // RMA preview (mode='rma'): the return-credit unit price (auto-filled from the
  // original SO, rep-editable & sticky), the per-line return reason, and the
  // source-SO document number for the provenance label.
  return_rate?: number | null;
  return_reason?: string | null;
  original_so_tranid?: string | null;
  original_so_id?: number | null;
  // RMA tie to an INVOICE (alternative source to the SO) + delivered-price audit:
  // return_freight_per_unit = the per-unit freight folded into return_rate;
  // return_source_has_promo = the tied SO/invoice carried a promo/discount line.
  original_invoice_id?: number | null;
  original_invoice_tranid?: string | null;
  return_freight_per_unit?: number | null;
  return_source_has_promo?: boolean | null;
  // DISCOUNT / MARKETING credit lines. line_kind marks the row kind; credit_amount
  // is the POSITIVE amount it credits (sent to NetSuite as a negative rate);
  // parent_line_id is the order line a DISCOUNT renders beneath (null for a
  // top-level MARKETING line).
  line_kind?: 'SO' | 'RMA' | 'DISCOUNT' | 'MARKETING' | 'CM' | 'PROMOTION' | null;
  credit_amount?: number | null;
  parent_line_id?: number | null;
  // A NetSuite-auto-applied PROMOTION line (READ-BACK). It stays line_kind='DISCOUNT'
  // so EVERY existing write/submit/persist path treats it EXACTLY as today (zero
  // write-behavior change); is_promo flips ONLY the rendering to a read-only promo
  // row. memo is NetSuite's per-line promo text ("$0.70 off per bag").
  is_promo?: boolean;
  memo?: string | null;
  // A MANUAL PROMOTION line (line_kind='PROMOTION'): the rep picked a promotion from
  // the list to apply to the order (the SLX analogue of NetSuite's Promotions sublist
  // row). It is NOT an SO item line — it carries no item_id; promotion_id becomes one
  // of manual_promotion_ids on create. NetSuite validates eligibility when it applies.
  promotion_id?: number | null;
  promotion_name?: string | null;
  // Which RMA this return line belongs to (1,2,...). NULL = group 1.
  rma_group?: number | null;
  // Which SO/CM preview TAB this order/credit line belongs to (1,2,...). NULL = 1.
  preview_group?: number | null;
  // DROP-SHIP (direct-ship) line (SO-PARITY-v1). When the rep toggles direct-ship
  // on, the row's item_id is swapped to the '-DS' variant, create_po is set, and
  // the PO vendor is captured; base_item_* preserve the original so it can revert.
  create_po?: boolean;
  po_vendor_id?: string | null;
  po_vendor_name?: string | null;
  base_item_id?: number | null;
  base_item_name?: string | null;
  // Per-line unit price ("Price each" = NetSuite line rate), rep-editable. Prefilled
  // from the item's listed NetSuite price (create) or the order line's current rate
  // (edit); sent as the line rate so a manual price edit is honored.
  unit_price?: number | null;
}

// A NetSuite DIS* item (SKU starts with 'DIS') — the option source for discount &
// marketing credit lines.
interface DiscountItem {
  id: string;
  itemid: string;
  displayname: string;
}

// Canonical wanted-unit set (Gate-1 orchestrator decision). The <select>
// offers exactly these; the base unit the server derives may differ (e.g.
// "pallets" -> "bags").
// 'unknown' (holistic Prompt-AI): offered so a rep can mark / the brain can set
// a line whose unit couldn't be determined from the conversation (needs_review).
const WANTED_UNITS = ['each', 'bags', 'bricks', 'blocks', 'tubs', 'buckets', 'case', 'tote', 'pallet', 'unknown'];

// Neutral placeholder shorthand stamped on a rep-added ORDER line when it's
// persisted on resolve. It's a non-null seed (message_items.shorthand is NOT
// NULL) that is NEVER a customer term, so resolving the line learns no shorthand
// mapping. It also tags the line as rep-added so a re-pick routes through the
// no-learn set_line_item path (mirrors the RMA 'Return item' placeholder).
const SO_LINE_PLACEHOLDER = 'Order item';

interface OrderItemEditorProps {
  messageId: number;
  customerId?: number | null;
  items?: MessageItem[];
  // The order message's updated_at. Changes when a customer's clarification
  // reply resolves a line server-side; we refetch on change so the open thread
  // shows the resolved item without a manual close/reopen.
  refreshKey?: string | number | null;
  onCreateSO: (items: EditableItem[], hotLoad: boolean, header?: SoHeaderFields, weightLb?: number) => void;
  soStatus: 'idle' | 'loading' | 'success' | 'error';
  soResult: string;
  // Order-edit mode (ORDER-EDIT-PLAN-v2). DEFAULT 'create' keeps the existing
  // new-order flow untouched. In 'edit' mode the action submits line changes to
  // an existing SO via onSubmitChanges instead of creating a new order. In 'rma'
  // mode this is the RMA (returns) preview: same sticky per-line editing plus a
  // return-price + reason column, and the primary action creates a Return
  // Authorization via onCreateRMA.
  mode?: 'create' | 'edit' | 'rma';
  // Hot Load pre-check (#8): true when any message in the thread has hot_load.
  // Seeds the Hot Load checkbox ONCE at mount; the rep can still un-check it
  // (initialize-once, no re-sync effect that clobbers a manual toggle).
  initialHotLoad?: boolean;
  onSubmitChanges?: (items: EditableItem[], hotLoad: boolean, header?: SoHeaderFields, weightLb?: number) => void;
  // Cancel (close) the loaded existing SO. Available whenever an order is loaded
  // in 'edit' mode (Update-SO mode), NOT only for AI-staged cancellations. The
  // human clicks it (after a confirm) to fire the only cancel write upstream.
  onCancelOrder?: () => void;
  // RMA mode primary action: create a NetSuite Return Authorization from the
  // resolved return lines (write-gated server-side until go-live).
  onCreateRMA?: (items: EditableItem[]) => void;
  // Multi-RMA: this editor instance shows ONE RMA group (a thread's returns split
  // into separate Return Authorizations). rmaGroup is which group (1,2,...) this
  // instance renders — its fetched lines are filtered to it. rmaGroups is every
  // group present on the thread (for the per-line "RMA #" move picker). onMoveLine
  // moves a line to another group (or a new one).
  rmaGroup?: number;
  rmaGroups?: number[];
  onMoveLine?: (lineId: number, toGroup: number) => void;
  // Multi-preview (SO/CM tabs): which preview group (1,2,...) this instance renders.
  // In 'rma' mode this aliases rmaGroup; in create/edit it is the SO preview_group.
  // Fetched thread lines are filtered to it. Undefined => group 1 (single-preview).
  group?: number;
  // Employee-originated thread (a rep working on behalf of a customer). Gates the
  // "Add Marketing Line" option — marketing-funds lines are only for employee
  // threads. employeeName is the rep's name, used to auto-match their DIS*
  // 'Marketing Funds' item (e.g. "TYLER FORD - Marketing Funds").
  isEmployeeThread?: boolean;
  employeeName?: string | null;
  // EDIT mode only: the SO's PRISTINE NetSuite lines (before any staged overlay),
  // used to diff the rep's manual edits into {op,item_id,shorthand,quantity} ops.
  // onPersistEdit receives those ops (debounced) whenever the rep changes a line,
  // so the parent can persist them as a pending order edit — making manual edits
  // survive close/reopen and be respected when Sally is prompted. An EMPTY ops
  // list means "reverted to the SO's current lines" (the parent discards the row).
  editBaseline?: Array<{ line?: number | null; item_id: number | null; item_name?: string | null; quantity?: number | null; rate?: number | null; item_type?: string | null }>;
  onPersistEdit?: (ops: Array<{ op: string; item_id: number | null; shorthand: string | null; quantity: number | null }>) => void;
  // EDIT mode only: surface the editor's CURRENT line set (live, every change) so
  // the parent can render a "current NetSuite order vs proposed" compare panel
  // (OrderDiffPanel). Distinct from onPersistEdit (which is debounced + only emits
  // qty/add/remove ops for persistence); this emits the full item rows so the
  // compare can also show price changes. Non-fatal / read-only for the parent.
  onItemsChange?: (items: EditableItem[]) => void;
  // EDIT mode: seed the header fields from the SO's CURRENT NetSuite header merged
  // with Sally's staged ship-method/ship-date change (thread 4988), so the preview
  // shows the real order header with her change pre-filled. editHeaderChangedKeys
  // lists the header fields she changed (for highlight); editHeaderApproxDate flags
  // a fuzzy date ("week of 8/17") the rep should confirm.
  editHeaderSeed?: SoHeaderFields | null;
  editHeaderChangedKeys?: string[];
  editHeaderApproxDate?: boolean;
  // EDIT mode: the NetSuite-auto-applied PROMOTION discount lines on the loaded SO
  // (getOrderPromoLines). Reconciled by line number against the existing negative-
  // rate lines so genuine promos (is_promo) render as READ-ONLY "Promotion" rows
  // bound to their item; rep-added discounts stay editable. Display-only.
  promoLines?: PromoLine[];
}

const OrderItemEditor: React.FC<OrderItemEditorProps> = ({
  messageId, customerId, items: initialItems, refreshKey, onCreateSO, soStatus, soResult,
  mode = 'create', initialHotLoad = false, onSubmitChanges, onCancelOrder, onCreateRMA,
  rmaGroup, rmaGroups, onMoveLine, group,
  isEmployeeThread = false, employeeName = null,
  editBaseline, onPersistEdit, onItemsChange, promoLines,
  editHeaderSeed, editHeaderChangedKeys, editHeaderApproxDate
}) => {
  // RMA preview: same sticky per-line editing as the SO preview, plus a
  // return-price + reason column and a "Create RMA" action.
  const isRma = mode === 'rma';
  // The thread-line fetcher for the current preview kind (SO vs RMA).
  const fetchLines = useCallback(
    () => (isRma ? getThreadRmaItems(messageId) : getThreadOrderItems(messageId)),
    [isRma, messageId]);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchingIdx, setSearchingIdx] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [learnedItems, setLearnedItems] = useState<Set<number>>(new Set());
  // Hot Load marker (#8): employee-flagged rush order. Tags the NetSuite SO
  // (custbody_jj_hotload) on Create / Submit.
  // Initialize-once from the thread's hot_load. No re-sync useEffect: after
  // mount the rep's manual toggle always wins (they can un-check a pre-check).
  const [hotLoad, setHotLoad] = useState(!!initialHotLoad);
  // RMA reason dropdown options (NetSuite 'Reason for Return' list). Fetched once
  // when this is the RMA preview; data-driven so a new NetSuite reason appears with
  // no code change. [] until loaded / for non-RMA modes.
  const [reasonOptions, setReasonOptions] = useState<{ id: string; name: string }[]>([]);
  // RMA source-tie picker: the customer's recent SOs + Invoices. Picking one prices
  // every return line at the DELIVERED price (base + per-unit freight) it was billed
  // at. [] until loaded / for non-RMA modes. sourceApplying gates the UI while a pick
  // fetches delivered prices and applies them.
  const [rmaSources, setRmaSources] = useState<RmaSource[]>([]);
  const [sourceApplying, setSourceApplying] = useState(false);
  const [sourceMsg, setSourceMsg] = useState<string>('');
  // DIS* item options (SKU starts with 'DIS') for the discount dropdown + the
  // per-employee marketing-funds match. Loaded once for the SO preview (not RMA).
  const [discountItems, setDiscountItems] = useState<DiscountItem[]>([]);
  // Per-employee 'Marketing Funds' items for the "Add Marketing Line" dropdown
  // (distinct from the DIS* discount items). Loaded once for the SO preview.
  const [marketingItems, setMarketingItems] = useState<DiscountItem[]>([]);
  // Whether the "Add Line" dropdown menu is open.
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // ── SO-preview parity (SO-PARITY-v1) ──────────────────────────────────────
  // The editable header fields (shipping details, terms & rep, ship-to address,
  // J&J logistics flags) + their option lists, and the per-item NetSuite weights
  // that drive the true total weight. Shown for create/edit (not RMA).
  const showOrderDetails = mode !== 'rma';
  const [bundle, setBundle] = useState<SoPreviewBundle | null>(null);
  const [header, setHeader] = useState<SoHeaderFields>({});
  const [weights, setWeights] = useState<Record<number, number>>({});
  // Per-item NetSuite list price → prefill each line's editable "Price each".
  const [prices, setPrices] = useState<Record<number, number>>({});
  // LEVELED-PRICE-v1: the customer's price-level quantity-break schedule per item —
  // the AUTHORITATIVE "Price each" (the price NetSuite will actually apply). Replaces
  // the customer-blind list price above. `priceLevel` is the customer's resolved
  // level ({id,name}) or null (no level / no customer -> lines blank + flag).
  const [leveledSchedules, setLeveledSchedules] = useState<Record<number, LeveledPriceSchedule>>({});
  // PROMO-PREVIEW (create mode): per-item promotion discount NetSuite would auto-apply
  // to the hypothetical order, from the promo_preview RESTlet (in-memory, no save).
  // Keyed by item_id → { amount (positive $), name }. Display-only, kept OUT of `items`
  // so it never touches submit/persist; rendered as a read-only row under each item.
  const [previewPromo, setPreviewPromo] = useState<Record<number, { amount: number; name: string | null }>>({});
  const [priceLevel, setPriceLevel] = useState<{ id: string; name: string | null } | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Seed the header from the customer defaults once PER CUSTOMER. Tracks the
  // customerId we last seeded so that assigning/reassigning the account AFTER the
  // editor opened (e.g. an ambiguous "Taylor Feed" resolved later) RE-seeds the
  // shipping/order-details from the NEW customer's defaults — the old one-time gate
  // seeded only once, so a customer assigned after first paint never populated the
  // header. `undefined` = never seeded (distinct from a real null/absent customer).
  const seededCustomerRef = useRef<number | null | undefined>(undefined);
  // Header fields the rep has manually edited (or that came from persisted prefs) —
  // a re-seed on customer change never overwrites these; the human's choice wins.
  const headerEditedFieldsRef = useRef<Set<string>>(new Set());
  // EDIT mode: guards the one-time header seed (SO current header + Sally's staged
  // ship-method/date change) per (SO, seed signature) so it doesn't re-run every
  // render, but re-seeds if the staged change updates.
  const headerSeededRef = useRef<string | null>(null);
  // SO-preview manual-correction PERSISTENCE. prefsLineRef holds the persisted
  // per-line overlay ({lineId: {unit_price, create_po, po_vendor_*, item swap}}) so
  // it can be re-applied every time the item set (re)loads. prefsLoadedRef gates
  // autosave — we NEVER save before the GET completes, or a first-paint effect
  // would clobber saved prefs with empty state. prefsSaveTimer debounces the PUT.
  const prefsLineRef = useRef<Record<number, any>>({});
  const prefsLoadedRef = useRef(false);
  const prefsSaveTimer = useRef<any>(null);
  // For the UNMOUNT FLUSH: latestPrefsRef holds the most recent corrections and
  // prefsUnsavedRef is true while a debounced save is still pending. When the rep
  // checks a box and immediately closes the thread (the reported bug), the debounce
  // timer would be cancelled on unmount and the edit lost — so on unmount we fire
  // the pending save immediately (the app stays alive, so the PUT completes).
  const latestPrefsRef = useRef<any>(null);
  const prefsUnsavedRef = useRef(false);
  const msgRef = useRef<number>(messageId);
  msgRef.current = messageId;
  const searchTimeout = useRef<any>(null);
  // Per-line debounce timers for the wanted qty/unit -> updateOrderLine PATCH,
  // keyed by the row's id, so edits on different lines don't cancel each other.
  const lineEditTimers = useRef<Record<number, any>>({});
  // Monotonic counter for synthetic negative ids on rep-added (blank) lines, so
  // each new line gets a stable unique id distinct from any real item id.
  const addCounter = useRef(0);
  // Set once the rep touches the line (qty/remove/search) so an auto-refresh
  // triggered by refreshKey never clobbers their unsaved edits.
  const dirtyRef = useRef(false);
  // Per-line complement to dirtyRef. dirtyRef is a SINGLE boolean for the whole
  // editor, and several handlers (item swap, correction) clear it synchronously —
  // so editing line B can drop the guard protecting line A's just-typed qty, and
  // the next 2s poll then whole-list-replaces line A back to server state (the
  // "field change appears then immediately disappears" flicker reps hit in the
  // modal). This set holds the ids of PERSISTED lines with an unreconciled local
  // qty/unit/item edit; the refetch merge (mergeFetched) preserves those lines'
  // in-progress rows even when dirtyRef is false, until each line's own PATCH
  // reconciles it and clears its mark. Price is NOT tracked here — it rides the
  // persisted-prefs overlay (applyLineOverlay), which mergeFetched already reapplies.
  const pendingLineIdsRef = useRef<Set<number>>(new Set());
  const markLinePending = (id: number | null | undefined) => {
    if (id != null && id > 0) pendingLineIdsRef.current.add(id);
  };
  const clearLinePending = (id: number | null | undefined) => {
    if (id != null) pendingLineIdsRef.current.delete(id);
  };
  // True after the first successful load — so a background refetch (poll / 5s
  // open-tick / refreshKey change) does NOT blank the editor to "Loading..." and
  // flicker; it keeps the current items visible and quietly swaps in the new set.
  const hasLoadedRef = useRef(false);
  // The messageId of the LATEST load request. A fetch's result is applied only
  // when this still matches its request — i.e. a DIFFERENT message superseded it.
  // A refreshKey poll tick re-runs the effect with the SAME messageId, so its
  // in-flight fetch still applies: this fixes the 2s-poll cancel race that left a
  // seed-less editor (the RMA preview) stuck on "Loading..." forever because every
  // tick cancelled the ~2s fetch just before it resolved.
  const loadMsgRef = useRef<number | null>(null);
  // Monotonic load SEQUENCE. loadMsgRef alone can't order two polls of the SAME
  // message (every 5s tick re-fires with the same messageId), so a SLOW/failed
  // thread-sweep that resolves LATE could clobber a newer poll that had the full
  // line set — the "preview went to 0/0" bug on thread #5112. Each fetch bumps
  // this at fire, captures it, and applies its result only if it is still the
  // latest — a stale (superseded) response is dropped.
  const loadSeqRef = useRef(0);

  // Overlay the PERSISTED per-line corrections (Price each + Direct ship + the
  // swapped -DS item) onto a freshly (re)loaded item set, so a background refetch
  // or a reopen restores exactly what the rep set. Keyed by the message_items line
  // id (stable for persisted lines). No-op until prefs have loaded.
  const applyLineOverlay = useCallback((list: EditableItem[]): EditableItem[] => {
    const ov = prefsLineRef.current || {};
    if (!ov || Object.keys(ov).length === 0) return list;
    return list.map(it => {
      const p = ov[it.id];
      if (!p) return it;
      const merged: EditableItem = { ...it };
      if (p.item_id != null) merged.item_id = p.item_id;
      if (p.resolved_item != null) merged.resolved_item = p.resolved_item;
      if (p.unit_price !== undefined) merged.unit_price = p.unit_price;
      if (p.create_po !== undefined) merged.create_po = p.create_po;
      if (p.po_vendor_id !== undefined) merged.po_vendor_id = p.po_vendor_id;
      if (p.po_vendor_name !== undefined) merged.po_vendor_name = p.po_vendor_name;
      if (p.base_item_id !== undefined) merged.base_item_id = p.base_item_id;
      if (p.base_item_name !== undefined) merged.base_item_name = p.base_item_name;
      return merged;
    });
  }, []);

  // Merge a freshly-fetched server line set over the current items WITHOUT
  // clobbering the rep's in-flight work. Two things are preserved: (1) rep-added
  // lines (synthetic negative ids) that live only in local state until Create,
  // and (2) any PERSISTED line with an unreconciled local edit (pendingLineIdsRef)
  // — its own PATCH will reconcile it and clear the mark. Everything else takes
  // the server row. Then the persisted-prefs overlay (Price each / Direct ship /
  // the swapped -DS item) is reapplied on top. This is the safety net that makes
  // the whole-list refetch immune to dirtyRef being cleared by an unrelated line.
  const mergeFetched = useCallback((prev: EditableItem[], fetched: any[]): EditableItem[] => {
    const repAdded = prev.filter(it => it.id < 0);
    const prevById = new Map(prev.map(it => [it.id, it]));
    const pending = pendingLineIdsRef.current;
    const merged = (Array.isArray(fetched) ? fetched : []).map((i: any) => {
      const p = prevById.get(i.id);
      if (p && pending.has(i.id)) return p;   // keep the rep's in-progress row
      return { ...i, removed: false };
    });
    return applyLineOverlay([...merged, ...repAdded]);
  }, [applyLineOverlay]);

  useEffect(() => {
    const reqMsgId = messageId;
    loadMsgRef.current = reqMsgId;
    const loadItems = async () => {
      // Don't overwrite a rep's in-progress edits when refetching on a refresh.
      if (dirtyRef.current) return;
      // EDIT mode: existing-order lines come from the parent (getSalesOrderLines):
      // {line, item_id, item_name, quantity, rate}. They ARE the source of truth,
      // so map them straight in and never sweep the thread here.
      if (mode === 'edit') {
        if (initialItems && initialItems.length > 0) {
          // NetSuite-auto-applied PROMOTION lines for this SO, keyed by line number.
          // A negative-rate line that matches an is_promo entry is rendered as a
          // READ-ONLY promo row (bound to its item, with NetSuite's memo) rather than
          // an editable rep discount. Rep-added discounts (no promo memo) are absent
          // here and stay editable.
          const promoByLine = new Map<number, PromoLine>();
          (promoLines || []).forEach(p => {
            if (p.is_promo && p.line != null) promoByLine.set(Number(p.line), p);
          });
          setItems(initialItems.map((i: any, idx: number) => {
            // An existing SO line with a NEGATIVE rate is a credit line (a discount
            // or marketing credit posts as a negative rate). Classify it so it
            // renders as a credit row — not a regular qty/unit order item. Marketing
            // vs discount is inferred from the item name ('marketing' -> MARKETING).
            // credit_amount is shown positive; the write path re-negates it.
            const rate = i.rate != null ? Number(i.rate) : null;
            const isCredit = rate != null && rate < 0;
            const promo = i.line != null ? promoByLine.get(Number(i.line)) : undefined;
            const nm = (i.item_name || '').toLowerCase();
            const lineKind = isCredit
              ? (nm.includes('marketing') ? 'MARKETING' : 'DISCOUNT')
              : null;
            return {
              id: i.line ?? -(idx + 1),
              shorthand: i.item_name || '',
              resolved_item: i.item_name || null,
              item_id: i.item_id ?? null,
              quantity: i.quantity ?? 1,
              unit: null,
              entered_qty: null,
              entered_unit: null,
              resolution_confidence: 1.0,
              removed: false,
              // Promo lines KEEP line_kind='DISCOUNT' (write path unchanged); is_promo
              // only flips rendering to the read-only promo row.
              line_kind: lineKind as any,
              is_promo: !!promo,
              credit_amount: isCredit ? Math.abs(rate as number) : null,
              // A promo row nests under the item line NetSuite discounted (parent_line);
              // rep discounts have no parent binding on load. Display-only (not written).
              parent_line_id: promo ? promo.parent_line : null,
              memo: promo ? promo.memo : null,
              // Prefill the "Price each" field with the order line's CURRENT rate
              // (not the item list price) so an edit shows the real order price.
              unit_price: (!isCredit && rate != null) ? rate : null,
            };
          }));
          hasLoadedRef.current = true;
        }
        return;
      }
      // CREATE mode: ALWAYS pull the FRESH reconciled thread preview so a Prompt-AI
      // resolution (or the poll) shows IN PLACE while the thread stays open. The
      // passed `initialItems` (from the messages poll) can LAG behind the backend's
      // reconciled message_items — the "view didn't update while open" bug — so we
      // use it only as a no-flicker placeholder on the FIRST paint, then swap in the
      // freshly fetched set on this and every subsequent refreshKey change.
      // Non-blanking: only show "Loading items..." on the very first load; a
      // background refetch keeps the current items on screen and quietly swaps.
      if (!hasLoadedRef.current) {
        if (initialItems && initialItems.length > 0) {
          setItems(applyLineOverlay(initialItems.map(i => ({ ...i, removed: false }))));
        } else {
          setLoading(true);
        }
      }
      // Order-guard for this fetch: only the LATEST create-mode load may apply its
      // result (see loadSeqRef). Captured BEFORE the await so a slow response knows
      // it's been superseded.
      const mySeq = ++loadSeqRef.current;
      try {
        let fetched = await fetchLines();
        // Multi-preview: this editor instance renders ONE group/tab, so keep only
        // its lines. RMA uses rma_group; SO/CM use preview_group. NULL => group 1.
        const wantGroup = isRma ? rmaGroup : group;
        if (wantGroup != null) {
          fetched = fetched.filter((i: any) => ((isRma ? i.rma_group : i.preview_group) ?? 1) === wantGroup);
        }
        // Don't apply an in-flight fetch that started BEFORE the rep began
        // editing: this fetch passed the dirtyRef guard at the top while dirty
        // was still false, but the rep has since touched the editor (e.g.
        // clicked "Add line"). Applying it now would clobber their unsaved work
        // — the "line appears then vanishes" bug. The next (post-edit) effect
        // run is guarded at the top and won't refetch until dirty clears.
        // ALSO drop this response if a NEWER load has since fired (mySeq stale) —
        // a slow poll must never overwrite a fresher one.
        if (loadMsgRef.current === reqMsgId && !dirtyRef.current
            && mySeq === loadSeqRef.current) {
          setItems(prev => {
            // NEVER blank a populated preview with an EMPTY background fetch. A
            // slow/failed thread-sweep (or a group that momentarily has no lines)
            // can come back empty; wiping the rep's populated order to "0/0" was
            // the thread-#5112 disappearing-lines bug. Keep what we have and let
            // the next successful poll refresh it. (The FIRST load has no persisted
            // lines to protect, so a genuine empty order still renders empty.)
            const fetchedEmpty = !Array.isArray(fetched) || fetched.length === 0;
            const hadPersisted = prev.some(it => it.id > 0 && !it.removed);
            if (fetchedEmpty && hadPersisted) return prev;
            // Preserve rep-added lines (synthetic negative ids). They live only
            // in local state — they're submitted on Create SO/RMA, never persisted
            // to the thread's items endpoint — so a background refetch would
            // otherwise drop every "Add line" row. Keep them appended to the fresh
            // server set.
            return mergeFetched(prev, fetched);
          });
          hasLoadedRef.current = true;
        }
      } catch (e) {
        // Keep the current items on a failed load (the backend now RETRIES the
        // thread sweep and returns an error rather than a misleading partial set,
        // so a throw here means "couldn't refresh" — never "the order is empty").
        console.error('Failed to load items:', e);
      } finally {
        // Clear the spinner unless a DIFFERENT message superseded this load.
        if (loadMsgRef.current === reqMsgId) setLoading(false);
      }
    };
    loadItems();
  }, [messageId, initialItems, refreshKey, mode, isRma, rmaGroup, group, fetchLines, applyLineOverlay, mergeFetched, promoLines]);

  // EDIT mode — persist the rep's MANUAL line edits (debounced). Diff the current
  // lines against the SO's PRISTINE NetSuite lines into add/remove/change_qty ops
  // and hand them to the parent, which stages them as a pending order edit. Fires
  // ONLY after a genuine rep edit (dirtyRef) so the initial load / staged-overlay
  // restore never re-persists. Credit lines are excluded (they're rep-added at
  // submit time, not part of the persisted SO-line working copy). An empty ops
  // list is still sent — it means "reverted", so the parent can clear the stage.
  const persistTimer = useRef<any>(null);
  useEffect(() => {
    if (mode !== 'edit' || !onPersistEdit) return;
    if (!dirtyRef.current) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      const isCredit = (k?: string | null) => k === 'DISCOUNT' || k === 'MARKETING';
      // Credit (DISCOUNT/MARKETING) baseline lines are rep-owned and diff-EXEMPT —
      // they carry a negative rate and, on some orders, several share ONE item_id
      // (e.g. SO120109's three DIS007 lines under item 12855). They must be dropped
      // from BOTH sides of the diff: `cur` already excludes them, so the baseline
      // must too, or every credit line reads as a phantom `remove`.
      const isCreditNsLine = (b: any) =>
        String(b?.item_type || '').toLowerCase() === 'discount'
        || (b?.rate != null && Number(b.rate) < 0);
      const base = (editBaseline || []).filter(b => !isCreditNsLine(b));
      const cur = items.filter(i => !i.removed && i.item_id != null && !isCredit(i.line_kind));
      const curIds = new Set(cur.map(c => Number(c.item_id)));
      const baseById = new Map<number, any>();
      base.forEach(b => { if (b.item_id != null) baseById.set(Number(b.item_id), b); });
      const ops: Array<{ op: string; item_id: number | null; shorthand: string | null; quantity: number | null }> = [];
      // Removed baseline lines (includes an item that was swapped out).
      base.forEach(b => {
        if (b.item_id != null && !curIds.has(Number(b.item_id))) {
          ops.push({ op: 'remove', item_id: Number(b.item_id), shorthand: b.item_name ?? null, quantity: null });
        }
      });
      // New lines (incl. swapped-in item) + quantity changes on kept lines.
      cur.forEach(c => {
        const b = baseById.get(Number(c.item_id));
        const q = Number(c.quantity) || 0;
        if (!b) {
          ops.push({ op: 'add', item_id: Number(c.item_id), shorthand: c.resolved_item ?? null, quantity: q });
        } else if (Number(b.quantity) !== q) {
          ops.push({ op: 'change_qty', item_id: Number(c.item_id), shorthand: c.resolved_item ?? null, quantity: q });
        }
      });
      onPersistEdit(ops);
    }, 600);
    return () => { if (persistTimer.current) clearTimeout(persistTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, mode, editBaseline, onPersistEdit]);

  // EDIT mode — surface the current line set to the parent for the live compare
  // panel. Fires on every `items` change (incl. the initial load, so the panel
  // starts from the true "after = current" and shows no phantom changes). Cheap +
  // read-only for the parent; no debounce needed.
  useEffect(() => {
    if (mode !== 'edit' || !onItemsChange) return;
    // Skip the initial pre-load empty state so the compare panel never flashes
    // "everything removed" before the SO lines land. Once loaded, real removals
    // still flow (items shrinks but hasLoadedRef stays true).
    if (!items.length && !hasLoadedRef.current) return;
    onItemsChange(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, mode, onItemsChange]);

  // Load the NetSuite 'Reason for Return' options once, for the RMA reason
  // dropdown. Only in RMA mode; non-fatal (an empty list just yields a dropdown
  // with the blank prompt). Not tied to refreshKey — the option set is static.
  useEffect(() => {
    if (!isRma) return;
    let cancelled = false;
    getReturnReasons()
      .then(opts => { if (!cancelled) setReasonOptions(opts || []); })
      .catch(e => console.error('Failed to load return reasons:', e));
    return () => { cancelled = true; };
  }, [isRma]);

  // Load the customer's recent SOs + Invoices for the RMA "tie to SO/Invoice" picker
  // (the source of the delivered price). Only in RMA mode with a known customer;
  // non-fatal (empty list => the picker just shows the blank prompt).
  useEffect(() => {
    if (!isRma || !customerId) { setRmaSources([]); return; }
    let cancelled = false;
    getCustomerSources(customerId)
      .then(src => { if (!cancelled) setRmaSources(src || []); })
      .catch(e => console.error('Failed to load RMA sources:', e));
    return () => { cancelled = true; };
  }, [isRma, customerId]);

  // Load the DIS* item family once (the discount dropdown + marketing-funds match).
  // SO preview only (create/edit); a return has no discount lines. Static set — not
  // tied to refreshKey. Non-fatal ([] just yields an empty dropdown).
  useEffect(() => {
    if (isRma) return;
    let cancelled = false;
    getDiscountItems()
      .then(opts => { if (!cancelled) setDiscountItems(opts || []); })
      .catch(e => console.error('Failed to load discount items:', e));
    getMarketingItems()
      .then(opts => { if (!cancelled) setMarketingItems(opts || []); })
      .catch(e => console.error('Failed to load marketing items:', e));
    return () => { cancelled = true; };
  }, [isRma]);

  // ── SO-preview parity: load the customer's default header values + every option
  // list (terms, locations, ship methods, sales reps, load ranks, route days,
  // addresses) once per customer, then SEED the editable header from the defaults
  // exactly once. Non-fatal — a blocked source just yields an empty dropdown.
  useEffect(() => {
    if (!showOrderDetails) return;
    let cancelled = false;
    // Option lists load even with no linked customer (they're account-global);
    // the customer defaults only prefill when a customer is present.
    getSoPreviewBundle(customerId)
      .then(b => {
        if (cancelled || !b) return;
        setBundle(b);
        // Seed once PER CUSTOMER: skip only if we already seeded for THIS exact
        // customerId. When the account changes (assigned/reassigned after the editor
        // opened), fall through and re-seed the customer-derived fields from the NEW
        // defaults — this is the fix for the header not updating after a late
        // customer assignment (Taylor Feed).
        if (seededCustomerRef.current === customerId) return;
        seededCustomerRef.current = customerId;
        const d = b.defaults || {};
        const plant = (b.locations || []).find(l => (l.name || '').toUpperCase() === 'PLANT');
        // Prefill only fields SLX otherwise leaves missing/wrong (the ones that
        // force NetSuite corrections). We intentionally do NOT auto-set the order
        // message or customForm — those are left to NetSuite's own defaults so this
        // stays purely additive (the rep can still type a message; the field maps
        // through if set). The rep's own edits (and persisted prefs) always win: a
        // field in headerEditedFieldsRef is never overwritten by a re-seed.
        setHeader(prev => {
          const edited = headerEditedFieldsRef.current;
          const next = { ...prev };
          // Account-global default: only fill when still empty (never override a pick).
          if (!edited.has('location_id') && next.location_id == null)
            next.location_id = plant ? plant.id : null;
          // Ship date: default to the MINIMUM of the standard 3-5 business-day
          // window — 3 business days out from today (skip Sat/Sun). Fill only when
          // empty so a rep's pick is never overwritten (Michael #2). Local-date
          // formatted (not UTC) so it never lands a day off near midnight.
          if (!edited.has('ship_date') && !next.ship_date) {
            const _sd = new Date();
            let _added = 0;
            while (_added < 3) {
              _sd.setDate(_sd.getDate() + 1);
              const _dow = _sd.getDay();
              if (_dow !== 0 && _dow !== 6) _added++;
            }
            const _p = (n: number) => String(n).padStart(2, '0');
            next.ship_date = `${_sd.getFullYear()}-${_p(_sd.getMonth() + 1)}-${_p(_sd.getDate())}`;
          }
          // Customer-specific: refresh to the NEW customer's default unless the rep
          // has edited it — so a late/changed account repopulates ship-to, terms,
          // rep and route day instead of keeping stale/empty values.
          if (!edited.has('ship_address_id')) next.ship_address_id = d.default_ship_address_id ?? null;
          if (!edited.has('terms_id')) next.terms_id = d.terms_id ?? null;
          if (!edited.has('sales_rep_id')) next.sales_rep_id = d.salesrep_id ?? null;
          if (!edited.has('route_day')) next.route_day = d.route_day_name ?? null;
          return next;
        });
      })
      .catch(e => console.error('Failed to load SO preview bundle:', e));
    return () => { cancelled = true; };
  }, [showOrderDetails, customerId]);

  // ── SO-preview parity: fetch NetSuite per-unit item weights for the resolved
  // lines and keep them cached. The TRUE total weight is Σ(weight × qty) from
  // these (an item's `weight` field), NOT the name-parsed token — so a
  // volume-named item ("...2.2CUFT", 0 name-weight) still weighs its real 35 lb.
  const resolvedItemIdsKey = items
    .filter(i => !i.removed && i.item_id
      && i.line_kind !== 'DISCOUNT' && i.line_kind !== 'MARKETING')
    .map(i => i.item_id).sort().join(',');
  useEffect(() => {
    if (!showOrderDetails) return;
    const ids = resolvedItemIdsKey
      ? resolvedItemIdsKey.split(',').map(Number).filter(Boolean) : [];
    const missing = ids.filter(id => !(id in weights) || !(id in prices));
    if (missing.length === 0) return;
    let cancelled = false;
    getItemWeightsAndPrices(missing)
      .then(({ weights: w, prices: p }) => {
        if (cancelled) return;
        if (w) setWeights(prev => ({ ...prev, ...w }));
        if (p) setPrices(prev => ({ ...prev, ...p }));
      })
      .catch(e => console.error('Failed to load item weights/prices:', e));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedItemIdsKey, showOrderDetails]);

  // ── LEVELED-PRICE-v1: fetch the customer's price-level quantity-break schedule
  // for the resolved lines. Re-fetches whenever the item set OR the customer
  // changes (the price DEPENDS on the customer's level), and clears when there's no
  // customer — so "Price each" only shows once a customer is defined, and any
  // manual qty change re-picks the tier locally (no round-trip). The whole map is
  // REPLACED each fetch (values are customer-specific — a stale merge would show the
  // prior customer's prices).
  useEffect(() => {
    if (!showOrderDetails) return;
    const ids = resolvedItemIdsKey
      ? resolvedItemIdsKey.split(',').map(Number).filter(Boolean) : [];
    if (!customerId || ids.length === 0) {
      setLeveledSchedules({});
      setPriceLevel(null);
      return;
    }
    let cancelled = false;
    getLeveledPrices(customerId, ids)
      .then(resp => {
        if (cancelled) return;
        setPriceLevel(resp.price_level || null);
        const m: Record<number, LeveledPriceSchedule> = {};
        Object.entries(resp.schedules || {}).forEach(([k, v]) => { m[Number(k)] = v; });
        setLeveledSchedules(m);
      })
      .catch(e => console.error('Failed to load leveled prices:', e));
    return () => { cancelled = true; };
  }, [resolvedItemIdsKey, customerId, showOrderDetails]);

  // Set/patch one header field (marks dirty so a background refetch won't clobber).
  const setHeaderField = <K extends keyof SoHeaderFields>(key: K, value: SoHeaderFields[K]) => {
    dirtyRef.current = true;
    // Remember the rep touched this field so a later re-seed (on a customer
    // change) never overwrites their choice.
    headerEditedFieldsRef.current.add(key as string);
    setHeader(prev => ({ ...prev, [key]: value }));
  };

  // EDIT mode: fields Sally staged a change to (thread 4988) get an amber ring so
  // the rep sees exactly what she changed. Cleared once the rep edits the field.
  const changedHeaderKeys = new Set(editHeaderChangedKeys || []);
  const headerFieldClass = (key: string) =>
    (changedHeaderKeys.has(key) && !headerEditedFieldsRef.current.has(key))
      ? 'ring-2 ring-amber-400 border-amber-400'
      : '';

  // ── SO-preview manual-correction PERSISTENCE ──────────────────────────────
  // Load the saved corrections once per message and overlay them: per-line onto
  // the loaded items, header/hot-load onto their state. prefsLoadedRef gates the
  // autosave so a first-paint effect can't clobber saved prefs with empty state.
  useEffect(() => {
    if (!showOrderDetails || !messageId) return;
    let cancelled = false;
    getSoPreviewPrefs(messageId)
      .then(p => {
        if (cancelled) return;
        p = p || {};
        prefsLineRef.current = (p.lines && typeof p.lines === 'object') ? p.lines : {};
        if (Object.keys(prefsLineRef.current).length) {
          setItems(prev => applyLineOverlay(prev));
        }
        if (p.header && typeof p.header === 'object') {
          // Persisted prefs are prior human choices — mark them edited so a
          // customer-change re-seed never overwrites them.
          Object.keys(p.header).forEach(k => headerEditedFieldsRef.current.add(k));
          setHeader(prev => ({ ...prev, ...p.header }));
        }
        if (typeof p.hot_load === 'boolean') setHotLoad(p.hot_load);
        prefsLoadedRef.current = true;
      })
      .catch(e => { console.error('SO-preview prefs load failed:', e); prefsLoadedRef.current = true; });
    return () => { cancelled = true; };
  }, [showOrderDetails, messageId, applyLineOverlay]);

  // EDIT mode: seed the header from the SO's CURRENT NetSuite header + Sally's
  // staged ship-method/ship-date change (thread 4988). Fills each seeded field
  // unless the rep has already edited it (headerEditedFieldsRef) or it's a saved
  // pref — so the rep's own choice always wins, but customer-default fallbacks are
  // replaced with the order's real values + the requested change. Re-seeds only
  // when the seed signature changes; never marks fields dirty (the staged change's
  // source of truth is the pending_order_edits set_header op, not prefs).
  useEffect(() => {
    if (mode !== 'edit' || !editHeaderSeed) return;
    const sig = `${refreshKey}:${JSON.stringify(editHeaderSeed)}`;
    if (headerSeededRef.current === sig) return;
    headerSeededRef.current = sig;
    setHeader(prev => {
      const edited = headerEditedFieldsRef.current;
      const next: any = { ...prev };
      for (const [k, v] of Object.entries(editHeaderSeed)) {
        if (v === null || v === undefined) continue;
        if (k === 'ship_method_name' || k === 'ship_date_approx') continue;
        if (edited.has(k)) continue;   // rep's saved/manual choice wins
        next[k] = v;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editHeaderSeed, refreshKey]);

  // Assemble the persistable prefs from current state: header, hot load, and the
  // per-line overrides (only lines that actually carry a manual correction).
  const buildPrefs = () => {
    const lines: Record<number, any> = {};
    items.forEach(it => {
      if (it.id == null) return;
      const hasOverride = (it.unit_price != null) || !!it.create_po || (it.base_item_id != null);
      if (!hasOverride) return;
      lines[it.id] = {
        unit_price: it.unit_price ?? null,
        create_po: !!it.create_po,
        po_vendor_id: it.po_vendor_id ?? null,
        po_vendor_name: it.po_vendor_name ?? null,
        item_id: it.item_id ?? null,
        resolved_item: it.resolved_item ?? null,
        base_item_id: it.base_item_id ?? null,
        base_item_name: it.base_item_name ?? null,
      };
    });
    return { header, hot_load: hotLoad, lines };
  };

  // Debounced autosave: whenever the rep has edited (dirtyRef) and prefs have
  // loaded, persist the current corrections. Runs off committed state (effect), so
  // it never captures a stale value. Keeps prefsLineRef in sync for later overlays.
  useEffect(() => {
    if (!showOrderDetails || !prefsLoadedRef.current || !dirtyRef.current) return;
    // Capture the latest corrections immediately so the unmount flush (below) can
    // still save them even if the thread is closed before the debounce fires.
    const prefs = buildPrefs();
    latestPrefsRef.current = prefs;
    prefsUnsavedRef.current = true;
    prefsLineRef.current = prefs.lines;
    if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
    prefsSaveTimer.current = setTimeout(() => {
      prefsUnsavedRef.current = false;
      saveSoPreviewPrefs(msgRef.current, prefs).catch(e => console.error('SO-preview prefs save failed:', e));
    }, 500);
    // NB: do NOT clear the timer on cleanup — a deps change reschedules it anyway,
    // and clearing on unmount is exactly what dropped the last edit. The unmount
    // flush effect handles the close-before-debounce case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, header, hotLoad, showOrderDetails, messageId]);

  // UNMOUNT FLUSH: if the rep closes the thread with an edit still pending (the
  // debounce hadn't fired), persist it now. Mount-once effect; the cleanup runs
  // only on real unmount. Fire-and-forget — collapsing a card keeps the app alive,
  // so the PUT completes. This is the fix for "checked a box, closed the thread,
  // had to re-check it".
  useEffect(() => {
    return () => {
      if (prefsSaveTimer.current) clearTimeout(prefsSaveTimer.current);
      if (prefsUnsavedRef.current && prefsLoadedRef.current && latestPrefsRef.current) {
        prefsUnsavedRef.current = false;
        saveSoPreviewPrefs(msgRef.current, latestPrefsRef.current).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-line "Direct ship" toggle. ON → swap the line to its '-DS' drop-ship
  // variant, flag create_po, and capture the PO vendor (so the backend spawns the
  // linked drop-ship PO — reproducing SO119111's Queen direct ship); also default
  // the order's ship method to DIRECT-SHIP. OFF → revert to the base item. When no
  // '-DS' variant exists we leave the line unchanged and note it (a rep can't force
  // a drop-ship on an item that has none).
  const [dsBusyIdx, setDsBusyIdx] = useState<number | null>(null);
  const handleToggleDirectShip = async (idx: number, on: boolean) => {
    dirtyRef.current = true;
    const row = items[idx];
    if (!row || !row.item_id) return;
    if (!on) {
      // Revert to the base item.
      setItems(prev => prev.map((it, i) => i === idx ? {
        ...it,
        item_id: it.base_item_id ?? it.item_id,
        resolved_item: it.base_item_name ?? it.resolved_item,
        create_po: false, po_vendor_id: null, po_vendor_name: null,
        base_item_id: null, base_item_name: null,
        unit_price: null,  // reprefill from the base item's list price
      } : it));
      return;
    }
    setDsBusyIdx(idx);
    try {
      const ds = await getDropshipVariant(row.item_id);
      if (!ds || !ds.ds_item_id) {
        // No DS variant — surface it briefly via needs_review, leave item as-is.
        setItems(prev => prev.map((it, i) => i === idx
          ? { ...it, create_po: false, po_vendor_name: 'no direct-ship variant' } : it));
        return;
      }
      setItems(prev => prev.map((it, i) => i === idx ? {
        ...it,
        base_item_id: it.base_item_id ?? it.item_id,
        base_item_name: it.base_item_name ?? it.resolved_item,
        item_id: ds.ds_item_id,
        resolved_item: ds.ds_name,
        create_po: true,
        po_vendor_id: ds.vendor_id,
        po_vendor_name: ds.vendor_name,
        // Auto-fill Price each = the drop-ship vendor/PO rate ÷ 0.9 (the J&J
        // direct-ship shipping-cost rule). Editable — if the actual vendor invoice
        // differs from the item's stored vendor rate, the rep adjusts it. Falls
        // back to the item's list-price prefill (null) when no PO rate is known.
        unit_price: (ds.po_rate != null && ds.po_rate > 0)
          ? Math.round((ds.po_rate / 0.9) * 1000) / 1000
          : null,
      } : it));
      // Default the order's ship method to DIRECT-SHIP for a drop-ship order.
      const direct = (bundle?.shipMethods || []).find(m => (m.name || '').toUpperCase() === 'DIRECT-SHIP');
      if (direct) setHeaderField('ship_method_id', direct.id);
    } catch (e) {
      console.error('direct-ship lookup failed:', e);
    } finally {
      setDsBusyIdx(null);
    }
  };

  // Wanted-qty typing: update the WANTED value locally only. The base
  // (quantity/unit) + conversion are recomputed server-side on blur.
  //
  // EDIT mode (pulled-in existing order): an SO line has NO entered/base split
  // and NO pallet conversion — the number the rep types IS the line's quantity,
  // i.e. exactly what gets submitted to NetSuite and summed into the total
  // weight. Write it straight to `quantity` so both update live, with no server
  // round-trip (edit-mode lines are NetSuite SO lines, not message_items).
  const handleQuantityChange = (idx: number, value: string) => {
    dirtyRef.current = true;
    markLinePending(items[idx]?.id);  // survive a poll if another line clears dirty
    const qty = parseInt(value) || 0;
    setItems(prev => prev.map((item, i) =>
      i === idx
        ? (mode === 'edit'
            ? { ...item, quantity: qty, entered_qty: null }
            : { ...item, entered_qty: qty })
        : item
    ));
  };

  // The effective WANTED pair for a row: entered_* when present, else fall back
  // to the derived base (old rows with no entered pair).
  const wantedQty = (item: EditableItem) => (item.entered_qty ?? item.quantity ?? 0);
  const wantedUnit = (item: EditableItem) => (item.entered_unit ?? item.unit ?? 'each');

  // Big (non-base) units whose typed count is NOT the base quantity the price tier
  // keys off — for those we use the server-maintained base `quantity` instead.
  const BIG_UNITS = ['pallet', 'pallets', 'plt', 'plts', 'pal', 'case', 'cases', 'cs'];
  // The BASE quantity a price tier is chosen by (breakpoints are in base units).
  // For a base-unit line the just-typed entered_qty is base (instant re-price);
  // for a pallet/case line use the converted base `quantity` (updated on blur).
  const baseQtyForPricing = (item: EditableItem): number => {
    const u = (wantedUnit(item) || '').toLowerCase().trim();
    if (item.entered_qty != null && !BIG_UNITS.includes(u)) return item.entered_qty;
    return item.quantity ?? wantedQty(item) ?? 0;
  };
  // LEVELED-PRICE-v1: the customer-price-level price for a line at its current base
  // qty — pick the tier with the greatest min_qty <= qty. null when there's no
  // schedule for the item (no customer / no per-level price).
  const leveledPrice = (item: EditableItem): number | null => {
    if (item.item_id == null) return null;
    const sch = leveledSchedules[item.item_id];
    if (!sch || !sch.tiers || sch.tiers.length === 0) return null;
    const q = baseQtyForPricing(item);
    let chosen = sch.tiers[0];
    for (const t of sch.tiers) {
      if (t.min_qty <= q && t.min_qty >= chosen.min_qty) chosen = t;
    }
    return chosen.unit_price ?? null;
  };
  // The effective per-unit price ("Price each"): the rep's manual edit (or, in edit
  // mode, the existing SO line rate) if set, else the customer's price-level +
  // quantity-break price. null => blank + flag (NetSuite auto-prices on Create).
  const effectivePrice = (item: EditableItem): number | null => {
    if (item.unit_price != null) return item.unit_price;
    const lv = leveledPrice(item);
    if (lv != null) return lv;
    return null;
  };
  // Why a line's Price each is blank / soft-flagged (null = no issue). Never sends
  // a wrong price — a blank line just lets NetSuite auto-price on Create.
  const priceIssue = (item: EditableItem): string | null => {
    if (isRma || isCreditKind(item.line_kind) || item.item_id == null) return null;
    if (item.unit_price != null) return null;            // rep override / edit rate
    if (leveledPrice(item) != null) {
      const sch = leveledSchedules[item.item_id];
      if (sch && sch.break_unknown)
        return 'Quantity-break threshold not confirmed for this item — NetSuite will apply it at order time.';
      return null;
    }
    if (!customerId) return 'Select a customer to see price-level pricing.';
    return 'No price-level price on file for this item — NetSuite will auto-price at order time; verify.';
  };
  const handleUnitPriceChange = (idx: number, value: string) => {
    dirtyRef.current = true;
    const v = value === '' ? null : parseFloat(value);
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, unit_price: (v == null || Number.isNaN(v)) ? null : v } : it));
  };

  // "Fill prices from invoice": read the vendor invoice attached to this thread,
  // match each order line to an invoice line, apply the ÷0.9 drop-ship rule, and
  // set each line's editable Price each. Rep reviews/edits before Create.
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState<string>('');
  const handleFillFromInvoice = async () => {
    const lines = items
      .filter(i => !i.removed && i.item_id
        && i.line_kind !== 'DISCOUNT' && i.line_kind !== 'MARKETING')
      .map(i => ({ index: i.id, name: i.resolved_item || i.shorthand || '' }));
    if (lines.length === 0) { setInvoiceMsg('No resolved lines to price'); return; }
    setInvoiceBusy(true);
    setInvoiceMsg('');
    try {
      const res = await fillInvoicePrices(messageId, lines);
      if (!res.attachment_found) {
        setInvoiceMsg(res.message || 'No invoice attachment found on this thread');
        return;
      }
      const byId = new Map<number, number>();
      (res.matches || []).forEach(m => {
        if (m.sale_price != null && m.index != null) byId.set(Number(m.index), m.sale_price);
      });
      if (byId.size === 0) {
        setInvoiceMsg(`Read ${res.pages || 1} page(s) but matched no line prices — enter manually`);
        return;
      }
      dirtyRef.current = true;
      setItems(prev => prev.map(it =>
        byId.has(it.id) ? { ...it, unit_price: byId.get(it.id)! } : it));
      setInvoiceMsg(`Filled ${byId.size} of ${lines.length} line${lines.length === 1 ? '' : 's'} from the invoice (÷${res.divisor ?? 0.9})`);
    } catch (e: any) {
      setInvoiceMsg(e?.response?.data?.error || e?.message || 'Could not read the invoice');
    } finally {
      setInvoiceBusy(false);
    }
  };

  // The customer's ORIGINAL qty+unit before a pallet->bags conversion, e.g.
  // "1 pallet", shown inline beside the qty box. Only renders when the box is
  // displaying the converted BASE (bags) rather than pallets already — otherwise
  // the box IS the original and there's nothing to add. The pallet count is the
  // stored original_pallet_qty when present (exact), else the inferred count the
  // thread-items endpoint derived from quantity / authoritative bags-per-pallet.
  const originalLabel = (item: EditableItem): string | null => {
    // Skip when the box is showing the customer's OWN entered pair (that value is
    // already the original) — only annotate lines displaying the converted base.
    if (item.entered_qty != null) return null;
    // Only when the box shows a CONVERTED base container (bags/tubs/blocks/...),
    // not an unconverted pallet line or an 'unknown' line.
    const bu = (wantedUnit(item) || '').toLowerCase().trim();
    if (!bu || bu === 'unknown'
        || ['pallet', 'pallets', 'plt', 'plts', 'pal'].includes(bu)) return null;
    const n = item.original_pallet_qty ?? item.inferred_pallet_qty;
    if (n != null && Number(n) > 0) {
      // Noun from the endpoint (case vs pallet); default pallet for legacy rows.
      const noun = item.orig_unit === 'case' ? 'case' : 'pallet';
      return `${n} ${noun}${Number(n) === 1 ? '' : 's'}`;
    }
    return null;
  };


  // Persist a line's wanted qty+unit and fold the authoritative BASE +
  // conversion back into that row. Debounced (~400ms, per line) like the search.
  // Skips unresolved (item_id null) rows and synthetic negative-id rows — they
  // have no persisted line to reconvert yet.
  //
  // Takes the row EXPLICITLY (plus optional field overrides for the value the
  // caller just changed). It must NOT read state via a setItems side-effect: a
  // functional-update callback runs during the NEXT render, not synchronously, so
  // the previous "let target; setItems(prev => {target = ...})" read left target
  // undefined at the point it was checked -> the function returned early and the
  // PATCH never fired (unit/qty edits appeared to save locally but never persisted
  // and never converted). Passing the row directly is race-free.
  const scheduleLineUpdate = (line: EditableItem, overrides?: Partial<EditableItem>) => {
    // EDIT mode lines are NetSuite SO lines, NOT message_items — their `id` is the
    // SO line number, so PATCHing /messages/:id/items by it would hit an unrelated
    // message_items row. Edit-mode qty/unit lives in local state and is written to
    // NetSuite only on "Submit changes"; never fire the message_items PATCH here.
    if (mode === 'edit') return;
    const target: EditableItem = { ...line, ...(overrides || {}) };
    const lineId = target.id;
    if (lineId < 0 || target.item_id == null) return;
    const body = {
      line_id: lineId,
      entered_qty: wantedQty(target),
      entered_unit: wantedUnit(target),
      item_id: target.item_id
    };
    if (lineEditTimers.current[lineId]) clearTimeout(lineEditTimers.current[lineId]);
    lineEditTimers.current[lineId] = setTimeout(async () => {
      try {
        const res = await updateOrderLine(messageId, body);
        setItems(prev => prev.map(it =>
          it.id === lineId ? {
            ...it,
            // Authoritative BASE from the server (downstream truth) — this is what
            // the qty/unit fields display.
            quantity: res.quantity ?? it.quantity,
            unit: res.unit ?? it.unit,
            // Take the entered pair from the server verbatim. For a PALLET
            // conversion the server returns NULL here so the row shows the
            // converted base (e.g. "12 tubs") in the fields, exactly like an
            // AI-auto-converted line — instead of "1 pallet". original_pallet_qty
            // preserves the pallet origin. Use ?? null (not ?? it.*) so a null
            // response actually clears the old pallet pair.
            entered_qty: res.entered_qty ?? null,
            entered_unit: res.entered_unit ?? null,
            original_pallet_qty: res.original_pallet_qty ?? it.original_pallet_qty,
            needs_review: !!res.needs_review
          } : it
        ));
        // Reconciled with server truth — a subsequent poll may now refresh this
        // line freely. (Left pending on failure so the unsaved edit stays protected.)
        clearLinePending(lineId);
      } catch (e) {
        console.error('Failed to update order line:', e);
      }
    }, 400);
  };

  // Big-unit sets for the OPTIMISTIC client-side conversion below.
  const PALLET_UNITS = ['pallet', 'pallets', 'plt', 'plts', 'pal'];
  const CASE_UNITS = ['case', 'cases', 'cs'];
  // Convert a MANUAL big-unit entry to base units IMMEDIATELY, without waiting on
  // the server PATCH (Michael 2026-08-03: "I shouldn't have to wait 20 seconds to
  // see it happen"). Returns the base-unit patch to apply to the row, or null when
  // we must NOT guess — in which case the row is left as-is and the server PATCH
  // (which re-runs the SAME rules authoritatively) decides. Mirrors the server
  // exactly: pallets convert ONLY on an authoritative (non-default) bags/pallet;
  // cases convert ONLY when a case pack is known (never invent one). The PATCH
  // still fires and its response overwrites these values with server truth.
  const optimisticConvert = (
    item: EditableItem, enteredQty: number | null | undefined, enteredUnit: string,
  ): Partial<EditableItem> | null => {
    const u = (enteredUnit || '').toLowerCase().trim();
    const isPallet = PALLET_UNITS.includes(u);
    const isCase = CASE_UNITS.includes(u);
    if (!isPallet && !isCase) return null;         // base unit -> no conversion
    const cnt = enteredQty;
    if (cnt == null || cnt <= 0) return null;      // no active count to convert
    const baseUnit = item.base_unit || item.unit || 'bags';
    if (isPallet) {
      const bpp = item.bags_per_pallet;
      // Only an AUTHORITATIVE pallet size converts (server never converts on the
      // default guess); unknown/default -> let the server decide.
      if (!bpp || bpp <= 0 || item.bags_per_pallet_is_default) return null;
      return {
        quantity: cnt * bpp, unit: baseUnit,
        entered_qty: null, entered_unit: null,
        original_pallet_qty: cnt, orig_unit: 'pallet', needs_review: false,
        conversion_display: `${cnt} pallet${cnt === 1 ? '' : 's'} → ${cnt * bpp} ${baseUnit}`,
      };
    }
    // case: never guess a pack we don't know.
    const cq = item.case_qty;
    if (!cq || cq <= 0) return null;
    return {
      quantity: cnt * cq, unit: baseUnit,
      entered_qty: null, entered_unit: null,
      original_pallet_qty: cnt, orig_unit: 'case', needs_review: false,
      conversion_display: `${cnt} case${cnt === 1 ? '' : 's'} → ${cnt * cq} ${baseUnit}`,
    };
  };

  // On qty blur: persist the row's current wanted pair (debounced). items[idx]
  // already reflects the latest entered_qty (onChange re-rendered before blur).
  // If the row's unit is STILL a big unit (rep picked pallets/cases then typed the
  // count), optimistically convert the just-typed count so the base shows at once.
  const handleQuantityBlur = (idx: number) => {
    const row = items[idx];
    if (mode !== 'edit' && row) {
      const opt = optimisticConvert(row, row.entered_qty, wantedUnit(row));
      if (opt) {
        markLinePending(row.id);
        setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...opt } : it));
      }
    }
    scheduleLineUpdate(row);
  };

  // On unit-dropdown change: set the wanted unit locally AND persist immediately,
  // passing the new unit as an explicit override so the PATCH carries it (no
  // reliance on the not-yet-applied state update). When the rep already typed a
  // count and picks a big unit, optimistically convert to base units on the spot.
  const handleUnitChange = (idx: number, value: string) => {
    dirtyRef.current = true;
    const row = items[idx];
    markLinePending(row?.id);  // survive a poll if another line clears dirty
    const opt = (mode !== 'edit' && row)
      ? optimisticConvert(row, row.entered_qty, value) : null;
    setItems(prev => prev.map((item, i) =>
      i === idx
        ? (opt ? { ...item, entered_unit: value, ...opt }
               : { ...item, entered_unit: value })
        : item
    ));
    scheduleLineUpdate(row, { entered_unit: value });
  };

  // ── RMA return-price / reason editing (mode='rma') ────────────────────────
  // Persist a rep's return-price and/or reason edit on one RMA line, debounced
  // per line. Routes to updateRmaLine (PATCH return_*), which sets
  // user_corrected=1 so the AI's rate backfill can never overwrite it (Michael:
  // human edits must STICK). Skips synthetic negative-id rows (no persisted line).
  const scheduleReturnUpdate = (line: EditableItem, overrides?: Partial<EditableItem>) => {
    const target: EditableItem = { ...line, ...(overrides || {}) };
    const lineId = target.id;
    if (lineId < 0) return;
    if (lineEditTimers.current[lineId]) clearTimeout(lineEditTimers.current[lineId]);
    lineEditTimers.current[lineId] = setTimeout(async () => {
      try {
        await updateRmaLine(messageId, {
          line_id: lineId,
          return_rate: target.return_rate ?? null,
          return_reason: target.return_reason ?? null,
          original_so_id: target.original_so_id ?? null,
          original_so_tranid: target.original_so_tranid ?? null,
          original_invoice_id: target.original_invoice_id ?? null,
          original_invoice_tranid: target.original_invoice_tranid ?? null,
          return_freight_per_unit: target.return_freight_per_unit ?? null,
          return_source_has_promo: target.return_source_has_promo ?? null,
        });
      } catch (e) {
        console.error('Failed to update RMA line:', e);
      }
    }, 400);
  };

  const handleReturnRateChange = (idx: number, value: string) => {
    dirtyRef.current = true;
    const rate = value === '' ? null : parseFloat(value);
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, return_rate: rate } : item
    ));
  };
  const handleReturnRateBlur = (idx: number) => scheduleReturnUpdate(items[idx]);

  // Reason is a SINGLE value for the whole RMA (NetSuite stores one reason per
  // Return Authorization — a body field). The one dropdown sets the same reason on
  // EVERY return line (kept in sync so it persists + so Create RMA picks it up),
  // and each line's PATCH sets user_corrected so the edit sticks.
  const handleRmaReasonSelect = (value: string) => {
    dirtyRef.current = true;
    const reason = value || null;
    setItems(prev => prev.map(item =>
      item.line_kind === 'RMA' ? { ...item, return_reason: reason } : item
    ));
    // Persist to every persisted RMA line (skip synthetic negative-id rows).
    items.forEach(item => {
      if (item.line_kind === 'RMA' && item.id > 0) {
        scheduleReturnUpdate(item, { return_reason: reason });
      }
    });
  };

  // Tie this RMA to a source SO / Invoice and price EVERY return line at the
  // DELIVERED price it was billed at (base rate + that unit's share of freight).
  // value = "salesorder:<id>" | "invoice:<id>". Fetches delivered prices for the
  // source and applies delivered_rate + freight + the tie + promo flag across the
  // group's lines (local state + persisted PATCH). Non-fatal.
  const handleRmaSourceSelect = async (value: string) => {
    if (!value) return;
    const [stype] = value.split(':');
    const src = rmaSources.find(s => `${s.type}:${s.id}` === value);
    if (!src) return;
    dirtyRef.current = true;
    setSourceApplying(true);
    setSourceMsg('');
    const isSo = stype === 'salesorder';
    const tie: Partial<EditableItem> = isSo
      ? { original_so_id: Number(src.id), original_so_tranid: src.tranid,
          original_invoice_id: null, original_invoice_tranid: null }
      : { original_invoice_id: Number(src.id), original_invoice_tranid: src.tranid,
          original_so_id: null, original_so_tranid: null };
    try {
      const { prices, has_promo } = await getDeliveredPrices(stype, String(src.id));
      const overridesByLineId: Record<number, Partial<EditableItem>> = {};
      let priced = 0, missing = 0;
      setItems(prev => prev.map(item => {
        if (item.line_kind !== 'RMA' || !item.item_id) return item;
        const info = prices[String(item.item_id)];
        const upd: Partial<EditableItem> = { ...tie, return_source_has_promo: has_promo };
        if (info && info.delivered_rate != null) {
          upd.return_rate = info.delivered_rate;
          upd.return_freight_per_unit = info.freight_per_unit ?? null;
          priced++;
        } else {
          missing++;
        }
        if (item.id > 0) overridesByLineId[item.id] = upd;
        return { ...item, ...upd };
      }));
      Object.entries(overridesByLineId).forEach(([lid, upd]) => {
        const line = items.find(i => i.id === Number(lid));
        if (line) scheduleReturnUpdate(line, upd);
      });
      setSourceMsg(missing > 0
        ? `Priced ${priced} line(s) at the delivered price from ${src.tranid}; ${missing} item(s) not on this ${isSo ? 'order' : 'invoice'}.`
        : `Priced ${priced} line(s) at the delivered price (base + freight) from ${src.tranid}.`);
    } catch (e) {
      console.error('Failed to apply RMA source:', e);
      setSourceMsg('Could not load delivered prices for that source.');
    } finally {
      setSourceApplying(false);
    }
  };

  const handleRemove = (idx: number) => {
    dirtyRef.current = true;
    const line = items[idx];
    // RMA lines are PERSISTED on add (real id), so removing one must HARD-DELETE
    // the message_items row — otherwise a blank/wrong return line (or an abandoned
    // new-RMA group) reappears on the next background refetch. Drop it locally and
    // delete server-side. (SO order lines are local until Create, so they keep the
    // reversible removed-toggle below.)
    if (isRma && line && line.id > 0) {
      setItems(prev => prev.filter((_, i) => i !== idx));
      deletePreviewLine(messageId, line.id)
        .catch(e => console.error('Failed to delete RMA line:', e));
      return;
    }
    setItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, removed: !item.removed } : item
    ));
  };

  // Append a blank line and immediately open item search on it so the rep
  // resolves it before submit.
  //
  // RMA mode: PERSIST the new return line right away (append_message_item,
  // line_kind='RMA', in THIS editor's rma_group) so it gets a REAL id. Every RMA
  // line then behaves identically — the per-line 'RMA #' picker, the return
  // rate/reason edits, and cross-group moves all key on a persisted id — and the
  // line survives the preview's ~2s background refetch. (Contrast: SO order lines
  // stay local under a synthetic negative id and are written to NetSuite on
  // Create SO.) Non-fatal: on a persist failure we fall back to a local row so the
  // rep can still work, mirroring the SO path.
  const appendBlankRow = (id: number) => {
    setItems(prev => {
      const next = [...prev, {
        id,
        shorthand: '',
        resolved_item: null,
        item_id: null,
        quantity: 1,
        unit: null,
        entered_qty: null,
        entered_unit: null,
        resolution_confidence: null,
        removed: false,
        ...(isRma ? { line_kind: 'RMA' as const, rma_group: rmaGroup ?? 1 } : {}),
      }];
      const newIdx = next.length - 1;
      // Open the search row on the freshly added line after state commits.
      setSearchingIdx(newIdx);
      setSearchQuery('');
      setSearchResults([]);
      return next;
    });
  };

  const handleAddLine = async () => {
    dirtyRef.current = true;
    if (isRma) {
      try {
        const res = await addPreviewLine(messageId, {
          line_kind: 'RMA',
          rma_group: rmaGroup ?? 1,
        });
        const newId = res?.id;
        if (newId != null) { appendBlankRow(Number(newId)); return; }
      } catch (e) {
        console.error('Failed to add RMA line (falling back to local):', e);
      }
    }
    addCounter.current += 1;
    appendBlankRow(-addCounter.current);
  };

  // ── DISCOUNT / MARKETING credit lines ─────────────────────────────────────
  // Both are DIS* items posted to the SO as a negative-rate credit. A DISCOUNT
  // renders under an order line (default DIS007); a MARKETING line renders at the
  // top of an employee thread, tied to that rep's 'Marketing Funds' DIS* item.
  const isCreditKind = (k?: string | null) =>
    k === 'DISCOUNT' || k === 'MARKETING';

  // A NetSuite-auto-applied PROMOTION row — read-only display over a DISCOUNT line
  // (write path unchanged; is_promo only flips rendering).
  const isPromo = (i?: { is_promo?: boolean } | null) => !!(i && i.is_promo);
  // A MANUAL promotion line (rep-added; carries a promotion_id, not an item).
  const isPromotionLine = (i?: { line_kind?: string | null } | null) =>
    (i?.line_kind ?? null) === 'PROMOTION';

  // The default discount item (DIS007 — "the most popular"), or null when the DIS*
  // list hasn't loaded / doesn't contain it (rep picks from the dropdown instead).
  const defaultDiscountItem = (): DiscountItem | null =>
    discountItems.find(d => (d.itemid || '').toUpperCase() === 'DIS007') || null;

  // Best DIS* match for the thread's employee: a "...Marketing Funds" item whose
  // displayname contains every token of the rep's name. Returns the sole/first
  // match, or null (then the rep picks it from the dropdown like a discount).
  const marketingItemForEmployee = (): DiscountItem | null => {
    const toks = (employeeName || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (toks.length === 0) return null;
    // Per-employee items are named "<NAME> - Marketing Funds", so all name tokens
    // appear in the displayname — auto-select the rep's item.
    const matches = marketingItems.filter(d => {
      const dn = (d.displayname || '').toLowerCase();
      return toks.every(t => dn.includes(t));
    });
    return matches[0] || null;
  };

  // In CREATE mode credit lines persist to message_items (survive refresh/reopen).
  // In EDIT mode they live locally and persist to NetSuite on "Submit changes"
  // (update_sales_order_lines) — edit rows are NetSuite lines, NOT message_items —
  // so the per-line handlers below skip the message_items writes.
  const persistCredits = mode === 'create';

  // Add a credit line and append it locally (ordering is applied at render time —
  // a DISCOUNT under its parent, a MARKETING line at the top). Marks dirty so a
  // background refetch can't clobber the rep's in-progress amount edit.
  const addCreditLine = async (
    lineKind: 'DISCOUNT' | 'MARKETING',
    seed: DiscountItem | null,
    parentLineId: number | null
  ) => {
    dirtyRef.current = true;
    const itemId = seed ? Number(seed.id) : null;
    const resolvedItem = seed ? seed.displayname : null;
    const appendRow = (id: number) => setItems(prev => [...prev, {
      id,
      shorthand: resolvedItem || (lineKind === 'MARKETING' ? 'Marketing' : 'Discount'),
      resolved_item: resolvedItem,
      item_id: itemId,
      quantity: 1,
      unit: null,
      entered_qty: null,
      entered_unit: null,
      resolution_confidence: 1.0,
      removed: false,
      line_kind: lineKind,
      credit_amount: null,
      parent_line_id: parentLineId,
    }]);
    // EDIT mode: local synthetic id; the row is written to NetSuite on submit.
    if (!persistCredits) {
      addCounter.current += 1;
      appendRow(-addCounter.current);
      return;
    }
    // CREATE mode: persist to message_items and key the row on the real id.
    try {
      const res = await addPreviewLine(messageId, {
        line_kind: lineKind,
        item_id: itemId,
        resolved_item: resolvedItem,
        credit_amount: null,
        parent_line_id: parentLineId,
      });
      const newId = res?.id;
      if (newId == null) return;
      appendRow(Number(newId));
    } catch (e) {
      console.error('Failed to add credit line:', e);
    }
  };

  const handleAddDiscount = (parentLineId: number) => {
    addCreditLine('DISCOUNT', defaultDiscountItem(), parentLineId);
  };

  // MANUAL PROMOTION LINE (Michael 2026-07-30): the rep adds a promotion from the
  // list to apply to the order (SLX analogue of NetSuite's Promotions sublist row).
  // Local-only (a synthetic negative id, not persisted to message_items) — its
  // promotion_id is sent as manual_promotion_ids on create; NetSuite validates
  // eligibility. Removal reuses handleRemoveCreditLine (id<0 → local drop, no persist).
  const handleAddPromotion = () => {
    setAddMenuOpen(false);
    dirtyRef.current = true;
    addCounter.current += 1;
    setItems(prev => [...prev, {
      id: -addCounter.current,
      shorthand: 'Promotion',
      resolved_item: null,
      item_id: null,
      quantity: 1,
      unit: null,
      entered_qty: null,
      entered_unit: null,
      resolution_confidence: 1.0,
      removed: false,
      line_kind: 'PROMOTION',
      promotion_id: null,
      promotion_name: null,
    }]);
  };

  const handlePromotionChange = (idx: number, promoIdStr: string) => {
    dirtyRef.current = true;
    const p = (bundle?.promotions || []).find(x => x.id === promoIdStr);
    setItems(prev => prev.map((it, i) =>
      i === idx
        ? { ...it, promotion_id: p ? Number(p.id) : null, promotion_name: p ? p.name : null }
        : it));
  };

  const handleAddMarketing = () => {
    setAddMenuOpen(false);
    addCreditLine('MARKETING', marketingItemForEmployee(), null);
  };

  // Rep picked a different discount item on a credit line — persist immediately.
  const handleCreditItemChange = (idx: number, itemIdStr: string) => {
    dirtyRef.current = true;
    // The picked option may come from either list (discount vs marketing).
    const picked = discountItems.find(d => d.id === itemIdStr)
      || marketingItems.find(d => d.id === itemIdStr);
    const itemId = picked ? Number(picked.id) : null;
    const resolvedItem = picked ? picked.displayname : null;
    const line = items[idx];
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, item_id: itemId, resolved_item: resolvedItem } : it));
    if (persistCredits && line && line.id > 0) {
      updatePreviewCredit(messageId, {
        line_id: line.id, item_id: itemId, resolved_item: resolvedItem,
      }).catch(e => console.error('Failed to update credit item:', e));
    }
  };

  // Credit amount typing: local only; persist (debounced) on blur.
  const handleCreditAmountChange = (idx: number, value: string) => {
    dirtyRef.current = true;
    const amt = value === '' ? null : parseFloat(value);
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, credit_amount: amt } : it));
  };
  const handleCreditAmountBlur = (idx: number) => {
    const line = items[idx];
    // EDIT mode: no message_items write — the amount is submitted to NetSuite on
    // "Submit changes". CREATE mode: debounce-persist to message_items.
    if (!persistCredits || !line || line.id < 0) return;
    if (lineEditTimers.current[line.id]) clearTimeout(lineEditTimers.current[line.id]);
    lineEditTimers.current[line.id] = setTimeout(() => {
      updatePreviewCredit(messageId, {
        line_id: line.id, credit_amount: line.credit_amount ?? null,
      }).catch(e => console.error('Failed to update credit amount:', e));
    }, 400);
  };

  // Remove a credit line: drop it locally. CREATE mode also hard-deletes the
  // persisted message_items row; EDIT mode just drops it from the submit set (the
  // NetSuite line is removed when "Submit changes" replaces the SO's line list).
  const handleRemoveCreditLine = (idx: number) => {
    dirtyRef.current = true;
    const line = items[idx];
    setItems(prev => prev.filter((_, i) => i !== idx));
    if (persistCredits && line && line.id > 0) {
      deletePreviewLine(messageId, line.id)
        .catch(e => console.error('Failed to delete credit line:', e));
    }
  };

  const handleSearchOpen = (idx: number) => {
    dirtyRef.current = true;
    setSearchingIdx(idx);
    setSearchQuery(items[idx].shorthand || '');
    setSearchResults([]);
  };

  const handleSearchClose = () => {
    setSearchingIdx(null);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchNetSuiteItems(value);
        setSearchResults(results);
      } catch (e) {
        console.error('Item search failed:', e);
      }
      setSearchLoading(false);
    }, 400);
  };

  const handleSelectItem = (idx: number, nsItem: any) => {
    const originalItem = items[idx];
    const newItemName = nsItem.displayname || nsItem.itemid;
    const newItemId = parseInt(nsItem.id);

    // Mark dirty so a background refetch (poll / openTick) can't clobber this
    // manual pick before the correction persists — matches every other edit
    // handler. Human corrections must stick (Michael). Also mark the line pending
    // so the pick survives even if another line's handler clears dirtyRef before
    // the correction reconciles (the correction refetch clears this mark).
    dirtyRef.current = true;
    markLinePending(originalItem?.id);

    setItems(prev => prev.map((item, i) =>
      i === idx ? {
        ...item,
        item_id: newItemId,
        resolved_item: newItemName,
        resolution_confidence: 1.0
      } : item
    ));
    handleSearchClose();

    // EDIT mode: an existing-order line is a NetSuite SO line, not a message_items
    // row — the item swap lives in local state and is written to NetSuite on
    // "Submit changes". Do NOT run the message_items correction/learning path (its
    // line id is an SO line number, and learning a shorthand off an existing order
    // isn't wanted). Release dirty so background polls resume.
    if (mode === 'edit') {
      dirtyRef.current = false;
      return;
    }

    // HAND-ADDED return line: its shorthand is a neutral placeholder (empty or
    // 'Return item'), NOT something the customer said — so persist the item WITHOUT
    // learning a shorthand mapping (the correction path would pollute the learned /
    // NetSuite shorthand dict). Persisted RMA lines carry a real id; a local fallback
    // row (negative id) just keeps the optimistic local state until Create RMA.
    const isPlaceholderRma = isRma
      && (!originalItem.shorthand || originalItem.shorthand === 'Return item');
    if (isPlaceholderRma) {
      if (originalItem.id > 0 && newItemId) {
        setPreviewLineItem(messageId, {
          line_id: originalItem.id,
          item_id: newItemId,
          resolved_item: newItemName,
        }).catch(e => console.error('Failed to set RMA line item:', e));
      }
      dirtyRef.current = false;
      return;
    }

    // HAND-ADDED order line (SO preview): make it behave EXACTLY like an auto-parsed
    // line by persisting it to message_items the moment it's resolved. A rep-added
    // line starts LOCAL (synthetic negative id) so a blank, item-less row never hits
    // the thread sweep; on resolve we persist it (op='add_line', line_kind='SO'),
    // swap the row to the returned real positive id, then fire the conversion PATCH.
    // From that point it is indistinguishable from an auto line: swept from the
    // thread (survives refetch + close/reopen), converted (pallet->bags), weighed,
    // read by Prompt AI, and — with a positive id — showing "+ Discount"/"Direct
    // ship". Its shorthand is a neutral placeholder, so no mapping is learned.
    if (!isRma) {
      const isNewLocal = originalItem.id < 0;
      const isNewPersisted = originalItem.id > 0 && originalItem.shorthand === SO_LINE_PLACEHOLDER;
      if (isNewLocal && newItemId) {
        // Build the resolved row synchronously (a setItems updater runs on the NEXT
        // render, so we must NOT read the swapped row back from it — see
        // scheduleLineUpdate's note). Persist, then swap the negative-id row in place
        // (dedging any positive-id copy a concurrent refetch may have already added).
        const swappedBase: EditableItem = {
          ...originalItem,
          item_id: newItemId,
          resolved_item: newItemName,
          shorthand: SO_LINE_PLACEHOLDER,
          resolution_confidence: 1.0,
        };
        addPreviewLine(messageId, {
          line_kind: 'SO',
          item_id: newItemId,
          resolved_item: newItemName,
          shorthand: SO_LINE_PLACEHOLDER,
          quantity: wantedQty(originalItem),
          unit: wantedUnit(originalItem),
          resolution_confidence: 1.0,
        }).then(res => {
          const newId = res?.id != null ? Number(res.id) : null;
          if (newId == null) return;
          const swapped: EditableItem = { ...swappedBase, id: newId };
          setItems(prev => {
            let placed = false;
            const out: EditableItem[] = [];
            prev.forEach(it => {
              if (it.id === originalItem.id || it.id === newId) {
                if (!placed) { out.push(swapped); placed = true; }
                return; // drop the negative-id original and any race-added copy
              }
              out.push(it);
            });
            if (!placed) out.push(swapped);
            return out;
          });
          // Fold in the authoritative base (pallet->bags) exactly like an auto line —
          // now that the row has a real id, the message_items PATCH will fire.
          scheduleLineUpdate(swapped);
        }).catch(e => {
          console.error('Failed to persist new order line:', e);
        }).finally(() => { dirtyRef.current = false; });
        return;
      }
      if (isNewPersisted && newItemId) {
        // Re-picking the item on an already-persisted rep-added line: stamp the new
        // item WITHOUT learning a shorthand (placeholder isn't a customer term).
        setPreviewLineItem(messageId, {
          line_id: originalItem.id,
          item_id: newItemId,
          resolved_item: newItemName,
        }).catch(e => console.error('Failed to set order line item:', e));
        dirtyRef.current = false;
        return;
      }
    }

    // Submit correction to learn the shorthand
    if (originalItem.shorthand && newItemId) {
      submitItemCorrection(messageId, {
        item_id: originalItem.id,
        shorthand: originalItem.shorthand,
        corrected_item_id: newItemId,
        corrected_item_name: newItemName,
        customer_id: customerId || undefined
      }).then(async () => {
        setLearnedItems(prev => new Set(prev).add(idx));
        // The correction persisted AND reconverted server-side (pallet->base, with
        // the entered pair cleared). Pull the AUTHORITATIVE state so the fields show
        // the converted base ('200 bags') + the 'N pallet(s)' label instead of the
        // stale optimistic entered pair — then RELEASE dirty so polls resume. Without
        // this the row was stuck on the pre-conversion display until a close/reopen
        // remounted the editor. NON-FATAL.
        try {
          const fresh = await fetchLines();
          // This corrected line is now reconciled server-side — drop its pending
          // mark so mergeFetched takes the AUTHORITATIVE converted row for it,
          // while still preserving rep-added lines and any OTHER line with an
          // unreconciled local edit.
          clearLinePending(originalItem.id);
          if (fresh) setItems(prev => mergeFetched(prev, fresh));
        } catch (err) {
          console.error('Post-correction refetch failed:', err);
        }
        dirtyRef.current = false;
      }).catch(e => {
        console.error('Failed to save correction:', e);
        dirtyRef.current = false;
      });
    } else {
      // No correction API call (no shorthand) — release dirty so polls resume.
      dirtyRef.current = false;
    }
  };

  // Credit lines don't count toward the "N/N resolved" order tally — they're
  // rep-owned credits, not order items to resolve.
  const activeItems = items.filter(i => !i.removed && !isCreditKind(i.line_kind) && !isPromotionLine(i));
  const resolvedCount = activeItems.filter(i => i.item_id).length;

  // ── PROMO-PREVIEW: in create mode, ask NetSuite's engine (via the promo_preview
  // RESTlet) which promotions it would auto-apply to THIS customer + line set, and at
  // what per-line amount — shown as read-only rows under each item. Edit mode uses the
  // read-back path instead (promos already on the saved order). Debounced; refetches
  // when the customer, the item set, or any base quantity changes; cleared when there's
  // no customer (mirrors leveled pricing — promos only show once a customer is defined).
  const promoPreviewKey = (mode === 'create' && customerId)
    ? activeItems.filter(i => i.item_id != null)
        .map(i => `${i.item_id}:${baseQtyForPricing(i)}`).join(',')
    : '';
  useEffect(() => {
    if (mode !== 'create' || !customerId || !promoPreviewKey) {
      setPreviewPromo({});
      return;
    }
    const lines = activeItems
      .filter(i => i.item_id != null)
      .map(i => ({ item_id: i.item_id as number, quantity: baseQtyForPricing(i) || 1 }));
    if (lines.length === 0) { setPreviewPromo({}); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      getPreviewPromoLines(customerId, lines)
        .then(res => {
          if (cancelled) return;
          const m: Record<number, { amount: number; name: string | null }> = {};
          res.forEach(r => {
            const amt = Number(r.discount_amount || 0);
            if (r.item_id != null && amt > 0) m[r.item_id] = { amount: amt, name: r.promo_name };
          });
          setPreviewPromo(m);
        })
        .catch(() => { if (!cancelled) setPreviewPromo({}); });
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
    // promoPreviewKey captures customer + item set + quantities; activeItems is a fresh
    // array each render so it's intentionally not a dep (would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoPreviewKey, customerId, mode]);

  // Read-only promo row shown under an order item in CREATE mode (the pre-create
  // counterpart of the edit-mode renderPromoRow). Null when this item has no promo.
  const renderPreviewPromoRow = (item: EditableItem) => {
    if (mode !== 'create' || item.item_id == null) return null;
    const p = previewPromo[item.item_id];
    if (!p || !(p.amount > 0)) return null;
    return (
      <div
        className="p-2 rounded border text-sm bg-amber-50 border-amber-200 ml-2 sm:ml-6"
        title="NetSuite promotion — auto-applied on the order (read-only preview)"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex-shrink-0 w-20 text-xs font-semibold uppercase tracking-wide text-amber-700">
            Promotion
          </span>
          <span className="flex-1 min-w-0 text-xs text-amber-800 truncate">
            {p.name || 'Promotional discount'}
          </span>
          <span className="flex-shrink-0 text-sm font-medium text-amber-800 tabular-nums">
            {`−$${p.amount.toFixed(2)}`}
          </span>
          <span className="flex-shrink-0 text-xs text-amber-500" title="Auto-applied by NetSuite on create">auto</span>
        </div>
      </div>
    );
  };

  // ── SHIP-TO GATE (Michael 2026-07-28) ──────────────────────────────────────
  // A sales order can't be placed without a VALIDATED ship-to address (NetSuite's
  // '[VALIDATED]' label convention). Mirror the server gate so the Create button
  // is blocked until a valid ship-to is chosen, with a message telling the rep why.
  // Only gates CREATE (placement); edits to an existing order are unaffected.
  const shipTo = bundle?.ship_to;
  const shipToValidatedIds = new Set((shipTo?.candidates || []).map(a => String(a.id)));
  const chosenShipAddr = (header.ship_address_id ?? '').toString().trim();
  const shipToOk = chosenShipAddr
    ? (shipToValidatedIds.size === 0 || shipToValidatedIds.has(chosenShipAddr))
    : (shipTo?.status === 'ok');
  // Only enforce once the bundle (with ship_to) has actually loaded, so we don't
  // flash a false block before the fetch resolves.
  const shipToBlocked = mode === 'create' && !!shipTo && !shipToOk;
  const shipToMessage = !shipToBlocked ? '' : (
    chosenShipAddr ? 'The selected ship-to address is not validated. Pick a [VALIDATED] address.'
    : shipTo?.status === 'ambiguous' ? 'This customer has multiple validated ship-to addresses — choose one below before placing the order.'
    : shipTo?.status === 'none' ? 'This customer has no validated ship-to address in NetSuite. Alana has been notified to add one; the order can’t be placed until then.'
    : 'Could not verify the ship-to address right now. Please retry in a moment.'
  );

  // ── SO-preview TOTAL WEIGHT ────────────────────────────────────────────────
  // Physical weight of the order, derived DIRECTLY from the NetSuite item weight
  // (the item's `weight` field, lb per stock/base unit) × the line's BASE quantity
  // (item.quantity is always the converted base — e.g. bags — even for a pallet
  // line). This replaces the old name-token parse, which returned 0 for volume-
  // named items ("...2.2CUFT") that still weigh a real 35 lb. The weights map is
  // fetched from /netsuite/itemweights; a name-token fallback keeps a number on
  // screen while the fetch is in flight or if the read fails. Credit / removed /
  // unresolved lines contribute nothing; a line with no weight from either source
  // is counted separately so the total is never silently under-reported. Hidden
  // for RMA. The backend recomputes the SAME way authoritatively on Create/Submit.
  let totalWeightLb = 0;
  let unweighedCount = 0;
  const weighedLines = activeItems.filter(i => i.item_id);
  weighedLines.forEach(i => {
    const nsWeight = i.item_id != null ? weights[i.item_id] : undefined;
    const per = (nsWeight != null && nsWeight > 0)
      ? nsWeight
      : perUnitLbFromName(i.resolved_item);
    const qty = Number(i.quantity) || 0;
    if (per != null) totalWeightLb += per * qty;
    else if (qty > 0) unweighedCount += 1;
  });
  const showTotalWeight = !isRma && weighedLines.length > 0;

  // The header + total weight to send with Create/Submit. Empty/null header keys
  // are dropped so we never blank a NetSuite field the rep didn't touch.
  const buildHeaderPayload = (): SoHeaderFields => {
    const out: SoHeaderFields = {};
    // J&J logistics flags are owned by NetSuite (the human team never changes them),
    // so SLX must never send them — sending false would force-uncheck the NetSuite
    // field. Strip them here so even a stale saved pref can't reach the payload.
    const NEVER_SEND = new Set(['pallet_jack_needed', 'seal_trailer', 'ship_blind_order']);
    (Object.keys(header) as (keyof SoHeaderFields)[]).forEach(k => {
      if (NEVER_SEND.has(k as string)) return;
      const v = header[k];
      if (v !== null && v !== undefined && v !== '') (out as any)[k] = v;
    });
    return out;
  };

  // Lines to send on Create/Submit: resolved, non-removed. We do NOT bake the
  // displayed leveled price into the rate (LEVELED-PRICE-v1 / Michael): a normal
  // line sends only its item+qty and lets NetSuite AUTO-PRICE from the customer's
  // price level + quantity break — the authoritative source. Only an explicit rate
  // rides along: a rep's manual "Price each" edit, an invoice-filled price, or (in
  // edit mode) the existing SO line rate — all of which live in unit_price and are
  // sent by toSoLinePayload. Credit lines carry their own negative rate.
  // Write path UNCHANGED: promo lines are line_kind='DISCOUNT', so they submit
  // exactly as today (SLX resends the existing negative-rate line; NetSuite re-applies
  // on save). is_promo only affects rendering, never the payload.
  // Manual PROMOTION lines are included so their promotion_id reaches createSalesOrder
  // (which extracts manual_promotion_ids); they carry no item_id, so toSoLinePayload
  // drops them from the actual SO line list — they never become order lines.
  const itemsForSubmit = (): EditableItem[] =>
    items.filter(i => !i.removed && (i.item_id || i.line_kind === 'PROMOTION'));

  // Display order: MARKETING lines first (top of list), then each order line
  // followed by its DISCOUNT children (incl. read-only promo rows), then any orphan
  // discount lines (parent not found). We iterate ORIGINAL indices so every per-line
  // handler still maps to the right items[idx].
  const orderedIdx: number[] = (() => {
    const marketing: number[] = [];
    const orders: number[] = [];
    const discountsByParent = new Map<number, number[]>();
    const orphans: number[] = [];
    items.forEach((it, i) => {
      // Manual promotion lines + marketing lines both sit at the top of the list.
      if (it.line_kind === 'MARKETING' || it.line_kind === 'PROMOTION') { marketing.push(i); return; }
      if (it.line_kind === 'DISCOUNT') {
        const pid = it.parent_line_id;
        const hasParent = pid != null && items.some(o => o.id === pid && !isCreditKind(o.line_kind));
        if (hasParent) {
          if (!discountsByParent.has(pid as number)) discountsByParent.set(pid as number, []);
          discountsByParent.get(pid as number)!.push(i);
        } else {
          orphans.push(i);
        }
        return;
      }
      orders.push(i);
    });
    const out: number[] = [...marketing];
    orders.forEach(i => {
      out.push(i);
      (discountsByParent.get(items[i].id) || []).forEach(k => out.push(k));
    });
    return [...out, ...orphans];
  })();

  // A MANUAL promotion line (rep-added): a promotion picker from the active-promotions
  // list. Applied to the order on Create (as manual_promotion_ids); NetSuite validates
  // eligibility. Violet, to distinguish from the amber read-back promo rows and the
  // indigo credit rows.
  const renderPromotionLine = (item: EditableItem, idx: number) => {
    const opts = bundle?.promotions || [];
    const stored = item.promotion_id != null ? String(item.promotion_id) : '';
    return (
      <div
        key={idx}
        className="p-2 rounded border text-sm bg-violet-50 border-violet-200"
        title="Manual promotion — applied to the order on Create (NetSuite validates eligibility)"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className="flex-shrink-0 w-20 text-xs font-semibold uppercase tracking-wide text-violet-700">
            Promotion
          </span>
          <div className="flex-1 min-w-0 sm:min-w-[14rem]">
            <select
              value={stored}
              onChange={e => handlePromotionChange(idx, e.target.value)}
              className="w-full text-xs border border-gray-300 rounded px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              title="Pick a promotion to apply to this order"
            >
              <option value="">&mdash; select promotion &mdash;</option>
              {/* Keep a stored promo visible even if the list hasn't loaded. */}
              {stored && !opts.some(p => p.id === stored) && (
                <option value={stored}>{item.promotion_name || stored}</option>
              )}
              {opts.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.code ? ` (${p.code})` : ''}
                </option>
              ))}
            </select>
          </div>
          <span className="flex-shrink-0 text-xs text-violet-500" title="Applied when the order is created">on create</span>
          <button
            onClick={() => handleRemoveCreditLine(idx)}
            className="flex-shrink-0 text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50 ml-auto"
            title="Remove promotion"
          >
            {'✕'}
          </button>
        </div>
      </div>
    );
  };

  // A NetSuite-auto-applied PROMOTION line: READ-ONLY, indented under the item it
  // discounts. Shows NetSuite's promo memo ("$0.70 off per bag") and the credit as a
  // negative amount. No controls — SLX never edits/writes these (NetSuite owns them);
  // the row exists to SHOWCASE the discount NetSuite will apply. Amber to distinguish
  // it from the indigo rep-editable credit rows.
  const renderPromoRow = (item: EditableItem, idx: number) => {
    const amt = item.credit_amount;
    return (
      <div
        key={idx}
        className="p-2 rounded border text-sm bg-amber-50 border-amber-200 ml-2 sm:ml-6"
        title="NetSuite promotion — auto-applied on the order (read-only)"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="flex-shrink-0 w-20 text-xs font-semibold uppercase tracking-wide text-amber-700">
            Promotion
          </span>
          <span className="flex-1 min-w-0 text-xs text-amber-800 truncate">
            {item.memo || item.resolved_item || 'Promotional discount'}
          </span>
          <span className="flex-shrink-0 text-sm font-medium text-amber-800 tabular-nums">
            {amt != null ? `−$${Number(amt).toFixed(2)}` : ''}
          </span>
          <span className="flex-shrink-0 text-xs text-amber-500" title="Auto-applied by NetSuite">auto</span>
        </div>
      </div>
    );
  };

  // A DISCOUNT/MARKETING credit line: a DIS* item dropdown + a positive credit
  // amount (posts to NetSuite as a negative rate). Discounts are indented under
  // their parent order line; marketing lines sit at the top of the list.
  const renderCreditRow = (item: EditableItem, idx: number) => {
    const isDiscount = item.line_kind === 'DISCOUNT';
    return (
      <div
        key={idx}
        className={`p-2 rounded border text-sm bg-indigo-50 border-indigo-200 ${isDiscount ? 'ml-2 sm:ml-6' : ''}`}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className="flex-shrink-0 w-20 text-xs font-semibold uppercase tracking-wide text-indigo-600">
            {isDiscount ? 'Discount' : 'Marketing'}
          </span>
          {/* Item dropdown — a DISCOUNT lists the DIS* items (default DIS007); a
              MARKETING line lists the per-employee 'Marketing Funds' items (auto-
              selects the rep's when the thread names one). */}
          <div className="flex-1 min-w-0 sm:min-w-[12rem]">
            {(() => {
              const opts = isDiscount ? discountItems : marketingItems;
              return (
            <select
              value={item.item_id != null ? String(item.item_id) : ''}
              onChange={e => handleCreditItemChange(idx, e.target.value)}
              className="w-full text-xs border border-gray-300 rounded px-1 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              title={isDiscount ? 'Discount item (NetSuite DIS*)' : 'Marketing funds item'}
            >
              <option value="">&mdash; select {isDiscount ? 'discount' : 'marketing'} item &mdash;</option>
              {/* Keep a stored item that isn't in the loaded list visible. */}
              {item.item_id != null && !opts.some(d => d.id === String(item.item_id)) && (
                <option value={String(item.item_id)}>{item.resolved_item || String(item.item_id)}</option>
              )}
              {opts.map(d => (
                <option key={d.id} value={d.id}>{d.displayname || d.itemid}</option>
              ))}
            </select>
              );
            })()}
          </div>
          {/* Credit amount — positive here, posts as a negative credit in NetSuite. */}
          <div className="flex-shrink-0 w-28 relative">
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.credit_amount ?? ''}
              onChange={e => handleCreditAmountChange(idx, e.target.value)}
              onBlur={() => handleCreditAmountBlur(idx)}
              placeholder="amount"
              className="w-full text-sm text-right border border-gray-300 rounded pl-4 pr-1.5 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
              title="Credit amount — positive here; posts to NetSuite as a negative credit"
            />
          </div>
          <span className="flex-shrink-0 text-xs text-indigo-500" title="Posts to NetSuite as a credit (negative)">credit</span>
          <button
            onClick={() => handleRemoveCreditLine(idx)}
            className="flex-shrink-0 text-xs px-2 py-1 rounded text-red-500 hover:bg-red-50 ml-auto"
            title="Remove line"
          >
            {'✕'}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="p-3 text-sm text-gray-500">Loading items...</div>;
  }

  // A preview is a FORM (Michael): an empty SO/RMA preview is NEVER a dead end. With
  // zero items we STILL render the full editor — the editable Shipping & order
  // details section, "+ Add line", and a greyed-out Create button — so a human (or
  // Sally) can build the order from scratch. Item parsing only PREFILLS lines; its
  // absence just means the form starts blank. (An empty in-thread hint appears
  // below.) Only 'edit' mode, which loads an existing SO's lines, is unaffected.
  // NOTE: the editor box below deliberately has NO overflow-x-hidden. Per CSS,
  // overflow-x:hidden forces overflow-y to compute as `auto`, which turned this box
  // into a clip/scroll container and CLIPPED the "+ Add line" menu and the per-line
  // item-search dropdown (they extend below the short empty-form content and vanished
  // — the rep saw a scrollbar they couldn't use). The modal panel already clips at
  // its edge, so wide content is contained without this box clipping its own popovers.
  return (
    <div className={`mt-3 p-3 rounded-lg ${isRma ? 'bg-orange-50' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700">{isRma ? `RMA ${rmaGroup ?? 1} (return)` : 'Order items'}</h4>
        <span className="text-xs text-gray-500">
          {resolvedCount}/{activeItems.length} resolved
        </span>
      </div>

      <div className="space-y-2">
        {activeItems.length === 0 && (
          <div className="text-xs text-gray-500 italic px-1 py-1">
            {isRma
              ? 'No return items yet — add the item(s) being returned below.'
              : 'No items yet — add order line(s) below, or fill in the details and create the order.'}
          </div>
        )}
        {orderedIdx.map((idx) => {
          const item = items[idx];
          if (isPromotionLine(item)) return renderPromotionLine(item, idx);
          if (isPromo(item)) return renderPromoRow(item, idx);
          if (isCreditKind(item.line_kind)) return renderCreditRow(item, idx);
          return (
          <React.Fragment key={idx}>
          <div
            className={`p-2 rounded border text-sm ${
              item.removed
                ? 'bg-red-50 border-red-200 opacity-50'
                : item.item_id
                  ? 'bg-white border-gray-200'
                  : 'bg-yellow-50 border-yellow-200'
            }`}
          >
          {/* Row wraps on narrow screens: the identity group (line id +
              shorthand + resolved item) takes the whole first line so the item
              name isn't crushed, and the controls wrap onto a second line. On
              wide screens flexbox keeps everything on one row. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            {/* IDENTITY GROUP — grows to fill; min width forces the controls to
                wrap below rather than squeezing the item name to a few chars. */}
            <div className="flex items-center gap-2 flex-1 min-w-0 sm:min-w-[15rem]">
            {/* 1. line ID (read-only, muted). Synthetic rep-added rows have a
                negative id -> show "new" instead of a negative number. */}
            <span
              className="hidden sm:block text-xs text-gray-400 font-mono flex-shrink-0 w-10 text-right"
              title={item.id < 0 ? 'New rep-added line' : `Line ${item.id}`}
            >
              {item.id < 0 ? 'new' : item.id}
            </span>

            {/* 2. shorthand (read-only, existing). */}
            <div className="w-1/5 min-w-0">
              <span className="text-gray-500 truncate block">{item.shorthand}</span>
            </div>

            <span className="text-gray-300 flex-shrink-0">&rarr;</span>

            {/* 3. order item (search-editable, existing UI/behavior). */}
            <div className="flex-1 min-w-0">
              {searchingIdx === idx ? (
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder="Search items..."
                    className="w-full text-sm border border-brand-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    autoFocus
                  />
                  {(searchResults.length > 0 || searchLoading) && (
                    <div className="absolute z-10 top-full right-0 w-[min(18rem,80vw)] sm:left-0 sm:w-auto mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchLoading && (
                        <div className="p-2 text-xs text-gray-400">Searching...</div>
                      )}
                      {searchResults.map((r: any) => (
                        <button
                          key={r.id}
                          onClick={() => handleSelectItem(idx, r)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium">{r.displayname || r.itemid}</span>
                          {r.description && (
                            <span className="text-gray-400 ml-2 text-xs truncate">{(r.description || '').substring(0, 40)}</span>
                          )}
                        </button>
                      ))}
                      {!searchLoading && searchResults.length === 0 && searchQuery.length >= 2 && (
                        <div className="p-2 text-xs text-gray-400">No items found</div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleSearchClose}
                    className="absolute right-1 top-1 text-gray-400 hover:text-gray-600 text-xs px-1"
                  >&times;</button>
                </div>
              ) : (
                <button
                  onClick={() => handleSearchOpen(idx)}
                  className={`text-left truncate block w-full px-1 rounded hover:bg-gray-100 ${
                    item.item_id ? 'text-gray-800 font-medium' : 'text-red-600 italic'
                  }`}
                  title="Click to change item"
                >
                  {item.resolved_item || 'Unresolved \u2014 click to search'}
                </button>
              )}
            </div>
            </div>{/* /IDENTITY GROUP */}

            {/* CONTROLS GROUP — badges + qty/unit (+ RMA price/reason) + remove.
                Wraps on phones (flex-wrap) so the wider RMA controls don't overflow
                the card; sm+ keeps them on one straight, right-anchored line
                (flex-nowrap), with the item-name column (flex-1 min-w-0) yielding
                the space. */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-end ml-auto">

            {item.resolution_confidence !== null && item.resolution_confidence < 0.8 && !item.removed && !learnedItems.has(idx) && (
              <span className="text-xs text-yellow-600 flex-shrink-0" title={`${Math.round((item.resolution_confidence || 0) * 100)}% confidence`}>
                &#9888;
              </span>
            )}

            {learnedItems.has(idx) && (
              <span className="text-xs text-green-600 flex-shrink-0" title="Correction saved — AI will remember this">
                &#10003; learned
              </span>
            )}

            {/* needs_review from the last conversion (e.g. unknown case pack) \u2014
                mirrors the low-confidence warning style. */}
            {item.needs_review && !item.removed && (
              <span className="text-xs text-yellow-600 flex-shrink-0" title="Needs review \u2014 conversion could not be confirmed">
                &#9888;
              </span>
            )}

            {/* Original pre-conversion qty+unit (e.g. "1 pallet"), inline just
                left of the qty box, showing what the base bag count was derived
                from. Only renders on intake-converted pallet lines. */}
            {originalLabel(item) && !item.removed && (
              <span
                className="flex-shrink-0 text-xs text-gray-500 whitespace-nowrap text-right"
                title={`Ordered as ${originalLabel(item)} \u2014 converted to ${item.quantity} ${item.unit || 'bags'}`}
              >
                {originalLabel(item)}
              </span>
            )}

            {/* 4. wanted qty (editable) \u2014 bound to entered_qty, falls back to the
                base quantity for old rows. Persists on blur. */}
            <div className="flex-shrink-0 w-16">
              <input
                type="number"
                min="0"
                value={wantedQty(item)}
                onChange={e => handleQuantityChange(idx, e.target.value)}
                onBlur={() => handleQuantityBlur(idx)}
                disabled={item.removed}
                className="w-full text-sm text-center border border-gray-300 rounded px-1 py-2 sm:py-1 focus:outline-none focus:ring-2 focus:ring-brand-500"
                title="Wanted quantity"
              />
            </div>

            {/* 5. wanted unit (editable <select>) \u2014 canonical set. Persists on
                change; falls back to the base unit for old rows. */}
            <div className="flex-shrink-0 w-24">
              <select
                value={wantedUnit(item)}
                onChange={e => handleUnitChange(idx, e.target.value)}
                disabled={item.removed}
                className="w-full text-xs border border-gray-300 rounded px-1 py-2 sm:py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                title="Wanted unit"
              >
                {/* Ensure a fallback unit that isn't in the canonical set still
                    shows as the selected option rather than silently blanking. */}
                {!WANTED_UNITS.includes(wantedUnit(item)) && (
                  <option value={wantedUnit(item)}>{wantedUnit(item)}</option>
                )}
                {WANTED_UNITS.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            {/* 5a2. PRICE EACH (SO preview, non-RMA) — the per-unit NetSuite line
                rate. LEVELED-PRICE-v1: prefilled from the customer's PRICE LEVEL +
                quantity break (the price NetSuite will apply), re-picked as the qty
                changes; editable so a rep can override. Blank + a ⚠ marker when it
                can't be priced (no customer / no per-level price) — NetSuite still
                auto-prices on Create. */}
            {!isRma && (() => {
              const issue = priceIssue(item);
              const isOverride = item.unit_price != null;
              return (
                <div className="flex-shrink-0 w-24 relative"
                     title={issue
                       ? issue
                       : (isOverride
                           ? 'Price each — manual override (sent as the NetSuite line rate).'
                           : `Price each — the customer's price-level price${priceLevel?.name ? ` (${priceLevel.name})` : ''}, auto-adjusted by quantity. Edit to override.`)}>
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    value={effectivePrice(item) ?? ''}
                    onChange={e => handleUnitPriceChange(idx, e.target.value)}
                    disabled={item.removed}
                    placeholder={customerId ? 'auto' : 'set customer'}
                    className={`w-full text-sm text-right border rounded pl-4 pr-5 py-2 sm:py-1 focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                      issue && effectivePrice(item) == null ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                  />
                  {issue && (
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-amber-500 text-xs pointer-events-none"
                          aria-label={issue}>⚠</span>
                  )}
                </div>
              );
            })()}

            {/* 5b. RMA per-line return PRICE (mode='rma'). Price is per line — each
                returned item has its own original-SO price. The RETURN REASON is a
                single RMA-level control (below), matching NetSuite (one reason per
                Return Authorization). Editable + STICKY (user_corrected server-side). */}
            {isRma && (() => {
              const frt = item.return_freight_per_unit;
              const rate = item.return_rate;
              const src = item.original_invoice_tranid || item.original_so_tranid;
              const base = (frt != null && rate != null) ? (Number(rate) - Number(frt)) : null;
              return (
                <div className="flex-shrink-0 w-28">
                  <div className="relative">
                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={item.return_rate ?? ''}
                      onChange={e => handleReturnRateChange(idx, e.target.value)}
                      onBlur={() => handleReturnRateBlur(idx)}
                      disabled={item.removed}
                      placeholder="price"
                      className="w-full text-sm text-right border border-gray-300 rounded pl-4 pr-1.5 py-2 sm:py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      title={`Delivered return credit — what the customer was billed (base rate + freight).`
                        + (base != null ? ` Base $${base.toFixed(4)} + freight $${Number(frt).toFixed(4)}.` : '')
                        + (src ? ` From ${src}.` : '')}
                    />
                  </div>
                  {base != null && (
                    <div className="text-[10px] text-gray-400 text-right mt-0.5 leading-tight">
                      base {base.toFixed(2)} + frt {Number(frt).toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* 5c. RMA group picker — on EVERY return line (a consistent "which RMA"
                field, not just persisted ones). Moves the line to another RMA (or a
                new one); splitting into multiple RMAs lets each carry its own reason
                (NetSuite stores one reason per Return Authorization). Persisted lines
                (real id) move server-side via onMoveLine (PATCH); a local fallback
                row moves in local state until it persists. */}
            {isRma && !item.removed && (
              <select
                value={String(item.rma_group ?? 1)}
                onChange={e => {
                  const v = e.target.value;
                  const toGroup = v === 'new'
                    ? (Math.max(0, ...(rmaGroups && rmaGroups.length ? rmaGroups : [1])) + 1)
                    : parseInt(v, 10);
                  if (!toGroup || toGroup === (item.rma_group ?? 1)) return;
                  dirtyRef.current = true;
                  if (item.id > 0 && onMoveLine) {
                    onMoveLine(item.id, toGroup);
                  } else {
                    setItems(prev => prev.map((it, i) =>
                      i === idx ? { ...it, rma_group: toGroup } : it));
                  }
                }}
                className="flex-shrink-0 w-20 text-xs border border-gray-300 rounded px-1 py-2 sm:py-1 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                title="Which RMA this line belongs to — move it to split into separate Return Authorizations"
              >
                {(rmaGroups && rmaGroups.length ? rmaGroups : [rmaGroup ?? 1]).map(g => (
                  <option key={g} value={String(g)}>RMA {g}</option>
                ))}
                <option value="new">+ new RMA</option>
              </select>
            )}

            {/* 6. X remove (existing). */}
            <button
              onClick={() => handleRemove(idx)}
              className={`flex-shrink-0 text-xs px-2 py-1 rounded ${
                item.removed
                  ? 'text-green-600 hover:bg-green-50'
                  : 'text-red-500 hover:bg-red-50'
              }`}
              title={item.removed ? 'Restore item' : 'Remove item'}
            >
              {item.removed ? 'Undo' : '\u2715'}
            </button>
            </div>{/* /CONTROLS GROUP */}
          </div>
          {/* Per-line actions (SO preview only): add a discount credit, and toggle
              DIRECT SHIP (drop-ship). Direct ship swaps the line to its '-DS'
              variant + POs from the vendor \u2014 reproducing a direct-ship order. */}
          {!isRma && !item.removed && item.item_id && item.id > 0 && (
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => handleAddDiscount(item.id)}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium px-1 py-0.5 rounded hover:bg-indigo-50"
                title="Add a discount credit under this line"
              >
                + Discount
              </button>
              <label
                className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
                title="Direct ship (drop-ship): swap to the '-DS' item and create a PO to the vendor"
              >
                <input
                  type="checkbox"
                  checked={!!item.create_po}
                  disabled={dsBusyIdx === idx}
                  onChange={e => handleToggleDirectShip(idx, e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="font-medium">Direct ship</span>
                {dsBusyIdx === idx && <span className="text-gray-400">\u2026</span>}
                {item.create_po && item.po_vendor_name && (
                  <span className="text-teal-700">\u2192 {item.po_vendor_name}</span>
                )}
                {!item.create_po && item.po_vendor_name === 'no direct-ship variant' && (
                  <span className="text-gray-400 italic">no -DS variant</span>
                )}
              </label>
            </div>
          )}
          </div>
          {renderPreviewPromoRow(item)}
          </React.Fragment>
          );
        })}
      </div>

      {/* SHIPPING & ORDER DETAILS — the editable header fields that make a placed
          SO match a normal NetSuite order (shipping details, terms & rep, ship-to
          address, J&J logistics flags) without post-hoc corrections. Prefilled
          from the customer's usual order; collapsible. Create/edit only (not RMA). */}
      {showOrderDetails && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={() => setDetailsOpen(o => !o)}
            className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900"
            title="Shipping method/date, location, freight, terms, ship-to, and J&J logistics flags"
          >
            <span className="text-xs">{detailsOpen ? '▾' : '▸'}</span>
            Shipping &amp; order details
          </button>
          {detailsOpen && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2.5">
              {/* Ship-from location */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Ship-from location
                <select
                  value={header.location_id ?? ''}
                  onChange={e => setHeaderField('location_id', e.target.value || null)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— default —</option>
                  {(bundle?.locations || []).map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </label>
              {/* Ship method */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Ship method{changedHeaderKeys.has('ship_method_id') && !headerEditedFieldsRef.current.has('ship_method_id') && <span className="text-amber-600"> · requested</span>}
                <select
                  value={header.ship_method_id ?? ''}
                  onChange={e => setHeaderField('ship_method_id', e.target.value || null)}
                  className={'text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ' + headerFieldClass('ship_method_id')}
                >
                  <option value="">— customer default —</option>
                  {(bundle?.shipMethods || []).map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </label>
              {/* Ship-to address */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Ship to
                <select
                  value={header.ship_address_id ?? ''}
                  onChange={e => setHeaderField('ship_address_id', e.target.value || null)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— default —</option>
                  {(bundle?.addresses || []).map(a => (
                    <option key={a.id} value={a.id}>
                      {a.label || [a.addr1, a.city, a.state].filter(Boolean).join(', ')}
                    </option>
                  ))}
                </select>
              </label>
              {/* Payment terms */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Terms
                <select
                  value={header.terms_id ?? ''}
                  onChange={e => setHeaderField('terms_id', e.target.value || null)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— default —</option>
                  {(bundle?.terms || []).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </label>
              {/* Sales rep — the employee list is restricted, so when it's empty we
                  keep the customer's default rep (still sent) as read-only text. */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Sales rep
                {bundle?.salesReps && bundle.salesReps.length > 0 ? (
                  <select
                    value={header.sales_rep_id ?? ''}
                    onChange={e => setHeaderField('sales_rep_id', e.target.value || null)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">— default —</option>
                    {bundle.salesReps.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm text-gray-700 border border-transparent px-0.5 py-1">
                    {bundle?.defaults?.salesrep_name
                      || (header.sales_rep_id ? `Rep #${header.sales_rep_id}` : '— customer default —')}
                  </span>
                )}
              </label>
              {/* Ship date */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Ship date{changedHeaderKeys.has('ship_date') && !headerEditedFieldsRef.current.has('ship_date') && (
                  <span className="text-amber-600"> · requested{editHeaderApproxDate ? ' (approx — confirm)' : ''}</span>
                )}
                <input
                  type="date"
                  value={header.ship_date ?? ''}
                  onChange={e => setHeaderField('ship_date', e.target.value || null)}
                  className={'text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ' + headerFieldClass('ship_date')}
                />
              </label>
              {/* Freight / shipping cost */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Freight (shipping $)
                <input
                  type="number" min="0" step="0.01"
                  value={header.shipping_cost ?? ''}
                  onChange={e => setHeaderField('shipping_cost', e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="carrier calc"
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>
              {/* Route day */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Route day
                <select
                  value={header.route_day ?? ''}
                  onChange={e => setHeaderField('route_day', e.target.value || null)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— none —</option>
                  {(bundle?.routeDays || []).map(d => (
                    <option key={d.id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </label>
              {/* Load type rank */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Load type rank
                <select
                  value={header.load_type_rank_id ?? ''}
                  onChange={e => setHeaderField('load_type_rank_id', e.target.value || null)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">— none —</option>
                  {(bundle?.loadTypeRanks || []).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              {/* Truckload slot weight */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">
                Truckload slot weight (lb)
                <input
                  type="number" min="0" step="1"
                  value={header.truckload_slot_weight ?? ''}
                  onChange={e => setHeaderField('truckload_slot_weight', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>
              {/* Customer message */}
              <label className="flex flex-col gap-0.5 text-xs text-gray-500 sm:col-span-2">
                Message on order
                <input
                  type="text"
                  value={header.message ?? ''}
                  onChange={e => setHeaderField('message', e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </label>
              {/* J&J logistics flags (pallet jack / seal trailer / ship blind) are
                  intentionally NOT surfaced here — NetSuite owns those defaults and
                  the human team never changes them, so Sally leaves them untouched
                  (they're also stripped from the header payload in buildHeaderPayload). */}
            </div>
          )}
        </div>
      )}

      {/* TOTAL WEIGHT — Σ(NetSuite item weight × base quantity). Hidden for RMA. */}
      {showTotalWeight && (
        <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
          <span className="font-medium text-gray-700">Total weight</span>
          <span className="font-semibold text-gray-900 text-right">
            {totalWeightLb.toLocaleString(undefined, { maximumFractionDigits: 2 })} lb
            {unweighedCount > 0 && (
              <span
                className="ml-2 text-xs font-normal text-gray-400"
                title="These lines have no weight in NetSuite or the item name, so they're not in the total"
              >
                ({unweighedCount} line{unweighedCount === 1 ? '' : 's'} without a weight)
              </span>
            )}
          </span>
        </div>
      )}

      <div className="mt-3 flex items-center flex-wrap gap-3">
        {/* RMA: a plain add-line button (returns have no discount/marketing lines).
            SO preview: an "Add line" dropdown — Add Order Line (a normal item line)
            and, on employee threads, Add Marketing Line (a top-of-list credit line
            tied to the rep's Marketing Funds account). */}
        {(isRma || !isEmployeeThread) ? (
          // RMA, and every non-employee SO preview, add a line DIRECTLY — no menu.
          // The dropdown's only extra option (Add Marketing Line) is employee-only,
          // so a one-item menu was pointless AND its popover was the thing getting
          // clipped. Clicking adds an in-flow blank line (the container scrolls to it).
          <button
            onClick={handleAddLine}
            className="text-sm text-brand-700 hover:text-brand-800 font-medium px-2 py-2 rounded-md hover:bg-brand-50"
          >
            + Add line
          </button>
        ) : (
          <div className="relative">
            <button
              onClick={() => setAddMenuOpen(o => !o)}
              className="text-sm text-brand-700 hover:text-brand-800 font-medium px-2 py-2 rounded-md hover:bg-brand-50 flex items-center gap-1"
            >
              + Add line <span className="text-xs">▾</span>
            </button>
            {addMenuOpen && (
              <>
                {/* click-away backdrop */}
                <div className="fixed inset-0 z-10" onClick={() => setAddMenuOpen(false)} />
                <div className="absolute z-20 left-0 top-full mt-1 min-w-[12rem] bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                  <button
                    onClick={() => { setAddMenuOpen(false); handleAddLine(); }}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-brand-50"
                  >
                    Add Order Line
                  </button>
                  {isEmployeeThread && (
                    <button
                      onClick={handleAddMarketing}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 text-indigo-700"
                      title="A marketing-funds credit line for this rep (employee threads only)"
                    >
                      Add Marketing Line
                    </button>
                  )}
                  {/* Manual promotion line — pick any promotion from the list to apply
                      on Create (create mode; the edit-submit path doesn't apply promos). */}
                  {mode === 'create' && !isRma && (
                    <button
                      onClick={handleAddPromotion}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-violet-50 text-violet-700"
                      title="Add a promotion from the list to apply to this order (NetSuite validates eligibility)"
                    >
                      Add Promotion
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {/* Hot Load marker (#8): tags the SO as a rush load in NetSuite. Not
            applicable to a return, so hidden in RMA mode. */}
        {!isRma && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none"
                 title="Tag this order as a Hot Load in NetSuite">
            <input
              type="checkbox"
              checked={hotLoad}
              onChange={(e) => { dirtyRef.current = true; setHotLoad(e.target.checked); }}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Hot Load
          </label>
        )}
        {/* Automatically Apply Promotions (Michael 2026-07-30): mirrors NetSuite's SO
            Promotions-subtab checkbox. Default OFF — nothing applies unless the rep
            checks this (or adds a manual promotion line). Create mode only (the
            edit-submit path doesn't apply promos). */}
        {!isRma && mode === 'create' && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer select-none"
                 title="Automatically apply eligible NetSuite promotions to this order when it's created">
            <input
              type="checkbox"
              checked={!!header.autoApplyPromotions}
              onChange={(e) => setHeaderField('autoApplyPromotions', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Auto-apply promotions
          </label>
        )}
        {/* Fill prices from the vendor invoice attached to the thread (drop-ship):
            reads the invoice, matches each line, applies ÷0.9, fills Price each.
            Rep reviews/edits before Create. Non-RMA only. */}
        {!isRma && (
          <button
            onClick={handleFillFromInvoice}
            disabled={invoiceBusy}
            className="flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-800 font-medium px-2 py-2 rounded-md hover:bg-teal-50 disabled:opacity-50"
            title="Read the vendor invoice attached to this thread and fill each line's Price each (÷0.9)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {invoiceBusy ? 'Reading invoice…' : 'Fill prices from invoice'}
          </button>
        )}
        {invoiceMsg && (
          <span className="text-xs text-gray-500 basis-full">{invoiceMsg}</span>
        )}
        {/* RMA SOURCE TIE: the original SO / Invoice this return credits. Picking one
            prices EVERY return line at the DELIVERED price the customer was billed
            (base rate + that unit's share of freight, read from the tied transaction). */}
        {isRma && soStatus === 'idle' && (() => {
          const tiedInv = items.find(i => i.line_kind === 'RMA' && i.original_invoice_id)?.original_invoice_id;
          const tiedSo = items.find(i => i.line_kind === 'RMA' && i.original_so_id)?.original_so_id;
          const selected = tiedInv ? `invoice:${tiedInv}` : (tiedSo ? `salesorder:${tiedSo}` : '');
          const sos = rmaSources.filter(s => s.type === 'salesorder');
          const invs = rmaSources.filter(s => s.type === 'invoice');
          return (
            <label className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
              <span className="text-gray-500">Tie to SO / Invoice:</span>
              <select
                value={selected}
                onChange={e => handleRmaSourceSelect(e.target.value)}
                disabled={sourceApplying}
                className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-72 disabled:opacity-50"
                title="Pick the original Sales Order or Invoice this return credits. Prices every line at the delivered price the customer was billed (base rate + freight)."
              >
                <option value="">{sourceApplying ? 'Pricing…' : 'pick the original SO or invoice'}</option>
                {sos.length > 0 && (
                  <optgroup label="Sales Orders">
                    {sos.map(s => (
                      <option key={`so-${s.id}`} value={`salesorder:${s.id}`}>
                        {s.tranid}{s.date ? ` — ${s.date}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {invs.length > 0 && (
                  <optgroup label="Invoices">
                    {invs.map(s => (
                      <option key={`inv-${s.id}`} value={`invoice:${s.id}`}>
                        {s.tranid}{s.date ? ` — ${s.date}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              {sourceMsg && <span className="text-xs text-gray-500 basis-full">{sourceMsg}</span>}
            </label>
          );
        })()}
        {/* Promo warning: the tied SO/Invoice carried a promotion/discount, so the
            delivered price (base + freight) is not net of that promo — the rep may
            need to adjust the return rate. */}
        {isRma && soStatus === 'idle'
          && items.some(i => i.line_kind === 'RMA' && i.return_source_has_promo) && (
          <div className="w-full text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-start gap-1.5">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span>The tied order carried a promotion/discount. The return rates above are base + freight only — adjust a line if the customer's net-of-promo price should be credited.</span>
          </div>
        )}
        {/* SINGLE RMA-level reason (NetSuite stores one reason per Return
            Authorization). Applies to THIS RMA; synced across its lines. Typable
            combobox: pick a NetSuite 'Reason for Return' OR type a custom reason —
            a custom (unlisted) reason is recorded in the RA memo on Create. */}
        {isRma && soStatus === 'idle' && (() => {
          const rmaReason = String(items.find(i => i.line_kind === 'RMA' && i.return_reason)?.return_reason ?? '');
          const listId = `rma-reasons-${messageId}-${rmaGroup ?? 1}`;
          return (
            <label className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700">
              <span className="text-gray-500">Reason for return:</span>
              <input
                type="text"
                list={listId}
                value={rmaReason}
                onChange={e => handleRmaReasonSelect(e.target.value)}
                placeholder="pick or type a reason"
                className="text-sm border border-gray-300 rounded px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-64"
                title="Reason for the return — pick a NetSuite reason or type a custom one (custom goes in the RA memo)"
              />
              <datalist id={listId}>
                {reasonOptions.map(o => (
                  <option key={o.id} value={o.name} />
                ))}
              </datalist>
            </label>
          );
        })()}
        {soStatus === 'idle' && isRma && (
          <button
            onClick={() => onCreateRMA && onCreateRMA(items.filter(i => !i.removed && i.item_id))}
            disabled={resolvedCount === 0}
            className={`text-sm text-white px-4 py-2 rounded-md flex items-center ${
              resolvedCount > 0
                ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2m0 0l3-3m-3 3l-3-3" />
            </svg>
            Create RMA ({resolvedCount} items)
          </button>
        )}
        {soStatus === 'idle' && mode === 'create' && shipToBlocked && (
          <div className="w-full text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2 flex items-start gap-1.5">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span>{shipToMessage}</span>
          </div>
        )}
        {soStatus === 'idle' && mode === 'create' && (
          <button
            onClick={() => onCreateSO(itemsForSubmit(), hotLoad, buildHeaderPayload(), Math.round(totalWeightLb))}
            disabled={resolvedCount === 0 || shipToBlocked}
            title={shipToBlocked ? shipToMessage : undefined}
            className={`text-sm text-white px-4 py-2 rounded-md flex items-center ${
              resolvedCount > 0 && !shipToBlocked
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Create sales order ({resolvedCount} items)
          </button>
        )}
        {soStatus === 'idle' && mode === 'edit' && (
          <button
            onClick={() => onSubmitChanges && onSubmitChanges(itemsForSubmit(), hotLoad, buildHeaderPayload(), Math.round(totalWeightLb))}
            disabled={resolvedCount === 0}
            className={`text-sm text-white px-4 py-2 rounded-md flex items-center ${
              resolvedCount > 0
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Submit changes ({resolvedCount} items)
          </button>
        )}
        {/* Cancel (close) the loaded order. Available whenever an existing SO is
            loaded in edit mode — not only for AI-staged cancels. Destructive
            (red) with a confirm before it fires the upstream close. */}
        {mode === 'edit' && onCancelOrder && soStatus !== 'success' && (
          <button
            onClick={() => {
              if (window.confirm('Close this order?')) onCancelOrder();
            }}
            disabled={soStatus === 'loading'}
            className="text-sm text-white px-4 py-2 rounded-md flex items-center bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Close order
          </button>
        )}
        {soStatus === 'loading' && (
          <span className="text-sm text-gray-500 animate-pulse">
            {mode === 'edit' ? 'Submitting changes...' : isRma ? 'Creating RMA...' : 'Creating SO...'}
          </span>
        )}
        {soStatus === 'success' && (
          <span className="text-sm text-green-600 font-medium">{soResult}</span>
        )}
        {soStatus === 'error' && (
          <span className="text-sm text-red-600">{soResult}</span>
        )}
        {resolvedCount < activeItems.length && resolvedCount > 0 && soStatus === 'idle' && (
          <span className="text-xs text-yellow-600">
            {activeItems.length - resolvedCount} unresolved item(s) will be skipped
          </span>
        )}
      </div>
    </div>
  );
};

export default OrderItemEditor;
